import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Play, Plus, Pencil, Trash2, Eye, Users, DollarSign, 
  Calendar, Loader2, Video, ArrowLeft, X, CheckCircle2,
  Upload, Image, Trophy, Clock, Radio, ChevronRight, 
  BarChart3, UserCircle, Mail, CreditCard, MessageSquare,
  Sparkles, Zap, GripVertical, ArrowUp, ArrowDown, MapPin,
  HelpCircle, Target, RefreshCw, Save, AlertCircle, Tag, Copy, Link, Percent,
  Shield, Eraser, Cloud, Tv, Lock, Unlock, Key, Globe, Crown, Monitor, Smartphone, Share2, Ticket, Film, ExternalLink, Megaphone,
  SearchCode, XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PromoManagement from '../components/PromoManagement';
import SEOAdmin from '../components/SEOAdmin';
import StripeAccountsManager from '../components/StripeAccountsManager';
import StreamControlPanel from '../components/StreamControlPanel';
import ErrorBoundary from '../components/ErrorBoundary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LivestreamAdminDashboard = () => {
  const navigate = useNavigate();
  const thumbnailInputRef = useRef(null);
  const mobileThumbnailInputRef = useRef(null);
  const socialImageInputRef = useRef(null);
  const promoVideoInputRef = useRef(null);
  const customLogoInputRef = useRef(null);
  const customWallpaperInputRef = useRef(null);
  const customWallpaperMobileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stripeAccounts, setStripeAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('events');
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showCustomerDetailModal, setShowCustomerDetailModal] = useState(false);
  const [showGrantTicketModal, setShowGrantTicketModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
  const [customerTickets, setCustomerTickets] = useState([]);
  const [loadingCustomerTickets, setLoadingCustomerTickets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resettingData, setResettingData] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingMobileThumbnail, setUploadingMobileThumbnail] = useState(false);
  const [uploadingSocialImage, setUploadingSocialImage] = useState(false);
  const [uploadingPromoVideo, setUploadingPromoVideo] = useState(false);
  const [uploadingCustomLogo, setUploadingCustomLogo] = useState(false);
  const [uploadingCustomWallpaper, setUploadingCustomWallpaper] = useState(false);
  const [uploadingCustomWallpaperMobile, setUploadingCustomWallpaperMobile] = useState(false);
  const [promoVideoProgress, setPromoVideoProgress] = useState(0);
  const [createStep, setCreateStep] = useState(1);
  const [showStreamControlPanel, setShowStreamControlPanel] = useState(false);
  const [controlPanelEvent, setControlPanelEvent] = useState(null);
  const [vodVideos, setVodVideos] = useState([]);
  
  // Event form data
  const [formData, setFormData] = useState({
    title: '', description: '', event_date: '', price: '', currency: 'GBP',
    thumbnail_url: '', mobile_thumbnail_url: '', social_share_image: '', promo_video_url: '', show_promo_video: false, stream_url: '', stripe_account: 'default',
    voting_enabled: false, voting_options: [], status: 'upcoming',
    // SEO fields
    seo_title: '', seo_description: '', seo_keywords: '', og_title: '', og_description: '',
    // Custom branding fields
    custom_logo_url: '', custom_signin_wallpaper_url: '', custom_signin_wallpaper_mobile_url: '', restricted_navigation: false, vod_video_id: ''
  });

  // Customer form data
  const [customerForm, setCustomerForm] = useState({
    email: '', password: '', full_name: '', username: ''
  });

  // Voting contestants
  const [votingContestants, setVotingContestants] = useState([
    { name: '', image: '', description: '' },
    { name: '', image: '', description: '' }
  ]);

  // Live viewers state
  const [liveViewers, setLiveViewers] = useState({ total_viewers: 0, viewers: [], locations: [] });

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Purchases state
  const [purchasesData, setPurchasesData] = useState(null);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(null);
  const [stripeBalance, setStripeBalance] = useState(null);

  // Voting questions state
  const [votingQuestions, setVotingQuestions] = useState([]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({ question: '', options: ['', ''], is_active: true });

  // Scorecard state
  const [scorecard, setScorecard] = useState({
    mode: 'open',
    contestants: [],
    battle_left: null,
    battle_right: null,
    is_visible: false,
    round_info: ''
  });
  const [savingScorecard, setSavingScorecard] = useState(false);
  const [newContestantName, setNewContestantName] = useState('');

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [savingPromo, setSavingPromo] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    event_id: '',
    usage_limit: '',
    expiry_date: '',
    is_active: true
  });

  // Moderator state
  const [moderators, setModerators] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);

  // Login wallpapers state
  const [wallpapers, setWallpapers] = useState([]);
  const [showWallpaperModal, setShowWallpaperModal] = useState(false);
  const [wallpaperForm, setWallpaperForm] = useState({ title: '', image_url: '', mobile_image_url: '', event_id: '' });
  const [uploadingWallpaper, setUploadingWallpaper] = useState(false);
  const [uploadingMobileWallpaper, setUploadingMobileWallpaper] = useState(false);
  const [savingWallpaper, setSavingWallpaper] = useState(false);
  const wallpaperInputRef = useRef(null);
  const mobileWallpaperInputRef = useRef(null);

  // Customer management state
  const [customerSubTab, setCustomerSubTab] = useState('users');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  // Winner announcement state
  const [votingResults, setVotingResults] = useState({});
  const [loadingVotingResults, setLoadingVotingResults] = useState(false);
  const [announcingWinner, setAnnouncingWinner] = useState(false);

  // Cloudflare Stream state
  const [streamSetup, setStreamSetup] = useState({});
  const [creatingStream, setCreatingStream] = useState(false);
  const [enablingSignedUrls, setEnablingSignedUrls] = useState(false);
  const [updatingExpiration, setUpdatingExpiration] = useState({});
  const [domainInput, setDomainInput] = useState({});
  const [savingDomains, setSavingDomains] = useState({});

  // Memoized filtered customer lists for performance
  const regularUsers = useMemo(() => 
    customers.filter(c => !c.is_admin && !moderators.some(m => m.id === c.id)),
    [customers, moderators]
  );
  
  const adminUsers = useMemo(() => 
    customers.filter(c => c.is_admin || moderators.some(m => m.id === c.id)),
    [customers, moderators]
  );

  const getAuthHeaders = useCallback(() => {
    // First try admin token, then try customer token if customer is admin
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      return { headers: { Authorization: `Bearer ${adminToken}` } };
    }
    const customerToken = localStorage.getItem('livestreamToken');
    if (customerToken) {
      return { headers: { Authorization: `Bearer ${customerToken}` } };
    }
    return { headers: {} };
  }, []);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async (eventId) => {
    if (!eventId) return;
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/livestream-admin/analytics/${eventId}`, getAuthHeaders());
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [getAuthHeaders]);

  // Fetch analytics summary
  const fetchAnalyticsSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/livestream-admin/analytics-summary`, getAuthHeaders());
      setAnalyticsSummary(res.data);
    } catch (err) {
      console.error('Error fetching analytics summary:', err);
    }
  }, [getAuthHeaders]);

  // Fetch purchases data
  const fetchPurchases = useCallback(async (eventId) => {
    if (!eventId) return;
    setLoadingPurchases(true);
    try {
      const res = await axios.get(`${API}/livestream-admin/purchases/${eventId}`, getAuthHeaders());
      setPurchasesData(res.data);
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setLoadingPurchases(false);
    }
  }, [getAuthHeaders]);

  // Fetch Stripe balance
  const fetchStripeBalance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/livestream-admin/stripe-balance`, getAuthHeaders());
      setStripeBalance(res.data);
    } catch (err) {
      console.error('Error fetching Stripe balance:', err);
    }
  }, [getAuthHeaders]);

  // Fetch VOD videos for linking dropdown
  const fetchVodVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vod-admin/videos`, getAuthHeaders());
      setVodVideos(Array.isArray(res.data?.videos) ? res.data.videos : []);
    } catch (err) {
      console.error('Error fetching VOD videos:', err);
    }
  }, [getAuthHeaders]);

  // Issue refund
  const handleRefund = async (ticketId) => {
    if (!window.confirm('Are you sure you want to issue a refund for this purchase? This action cannot be undone.')) {
      return;
    }
    setProcessingRefund(ticketId);
    try {
      const res = await axios.post(`${API}/livestream-admin/refund/${ticketId}`, {}, getAuthHeaders());
      alert(`Refund successful! Refund ID: ${res.data.refund_id}`);
      // Refresh purchases
      if (selectedEvent) {
        fetchPurchases(selectedEvent.id);
      }
    } catch (err) {
      alert(`Refund failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setProcessingRefund(null);
    }
  };

  // Reset all data (revenue, purchases, chat, analytics)
  const handleResetAllData = async () => {
    setResettingData(true);
    try {
      const res = await axios.delete(`${API}/livestream/admin/reset-all-data`, getAuthHeaders());
      alert(`Data reset successful!\n\nDeleted:\n- ${res.data.details.tickets_deleted} tickets/purchases\n- ${res.data.details.chat_messages_deleted} chat messages\n- ${res.data.details.viewers_deleted} viewer records\n- ${res.data.details.polls_deleted} polls\n- ${res.data.details.events_reset} events reset`);
      setShowResetConfirm(false);
      // Refresh all data
      loadData();
    } catch (err) {
      alert(`Reset failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setResettingData(false);
    }
  };

  useEffect(() => {
    // Check if user has any token (admin or customer)
    const adminToken = localStorage.getItem('adminToken');
    const customerToken = localStorage.getItem('livestreamToken');
    
    if (!adminToken && !customerToken) {
      // No tokens at all, redirect to admin login
      navigate('/admin');
      return;
    }
    
    // Try to load data - if unauthorized, redirect to admin login
    loadData();
  }, [navigate]);

  useEffect(() => {
    if (selectedEvent && activeTab === 'viewers') {
      loadLiveViewers();
      const interval = setInterval(loadLiveViewers, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedEvent, activeTab]);

  // Only load voting questions and scorecard when event changes AND on relevant tabs
  useEffect(() => {
    if (selectedEvent && (activeTab === 'voting' || activeTab === 'scorecard' || activeTab === 'events' || activeTab === 'viewers')) {
      loadVotingQuestions();
      loadScorecard();
    }
  }, [selectedEvent, activeTab]);

  // Load analytics and purchases when switching tabs or selecting event
  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalyticsSummary();
      if (selectedEvent) {
        fetchAnalytics(selectedEvent.id);
      }
    }
  }, [activeTab, selectedEvent, fetchAnalytics, fetchAnalyticsSummary]);

  useEffect(() => {
    if (activeTab === 'purchases') {
      fetchStripeBalance();
      if (selectedEvent) {
        fetchPurchases(selectedEvent.id);
      }
    }
  }, [activeTab, selectedEvent, fetchPurchases, fetchStripeBalance]);

  // Preload all data on initial load to avoid tab switching delays
  const loadData = async () => {
    setLoading(true);
    try {
      // Load ALL data in parallel at once
      const [statsRes, eventsRes, customersRes, promoRes, modsRes, wallpapersRes, stripeAccountsRes] = await Promise.all([
        axios.get(`${API}/livestream/admin/dashboard`, getAuthHeaders()),
        axios.get(`${API}/livestream/admin/events`, getAuthHeaders()),
        axios.get(`${API}/livestream/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/livestream/admin/promo-codes`, getAuthHeaders()).catch(() => ({ data: { promo_codes: [] } })),
        axios.get(`${API}/livestream/admin/moderators`, getAuthHeaders()).catch(() => ({ data: { moderators: [] } })),
        axios.get(`${API}/login-settings/wallpapers`).catch(() => ({ data: { wallpapers: [] } })),
        axios.get(`${API}/livestream/admin/stripe-accounts/list-for-select`, getAuthHeaders()).catch(() => ({ data: { accounts: [] } }))
      ]);
      
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
      setCustomers(customersRes.data.customers || []);
      setPromoCodes(promoRes.data.promo_codes || []);
      setModerators(modsRes.data.moderators || []);
      setStripeAccounts(stripeAccountsRes.data.accounts || [{ account_name: 'default', display_name: 'Default (Main LTV Account)' }]);
      setWallpapers(wallpapersRes.data.wallpapers || []);
      
      // Auto-select first live event
      const liveEvent = eventsRes.data.events?.find(e => e.status === 'live');
      if (liveEvent) setSelectedEvent(liveEvent);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const loadLiveViewers = async () => {
    if (!selectedEvent) return;
    try {
      const res = await axios.get(`${API}/livestream/admin/viewers/${selectedEvent.id}`, getAuthHeaders());
      setLiveViewers({
        total_viewers: res.data?.total_viewers || 0,
        viewers: Array.isArray(res.data?.viewers) ? res.data.viewers : [],
        locations: Array.isArray(res.data?.locations) ? res.data.locations : []
      });
    } catch (err) {
      console.error('Error loading viewers:', err);
    }
  };

  const loadVotingQuestions = async () => {
    if (!selectedEvent) return;
    try {
      const res = await axios.get(`${API}/livestream/admin/voting/${selectedEvent.id}/questions`, getAuthHeaders());
      setVotingQuestions(Array.isArray(res.data?.questions) ? res.data.questions : []);
    } catch (err) {
      console.error('Error loading questions:', err);
    }
  };

  const loadScorecard = async () => {
    if (!selectedEvent) return;
    try {
      const res = await axios.get(`${API}/livestream/admin/scorecard/${selectedEvent.id}`, getAuthHeaders());
      const data = res.data?.scorecard;
      if (data) {
        setScorecard({
          mode: data.mode || 'open',
          contestants: Array.isArray(data.contestants) ? data.contestants : [],
          battle_left: data.battle_left || null,
          battle_right: data.battle_right || null,
          is_visible: data.is_visible || false,
          round_info: data.round_info || ''
        });
      }
    } catch (err) {
      console.error('Error loading scorecard:', err);
    }
  };

  // Promo code functions
  const loadPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/livestream/admin/promo-codes`, getAuthHeaders());
      setPromoCodes(res.data.promo_codes || []);
    } catch (err) {
      console.error('Error loading promo codes:', err);
    }
  };

  const resetPromoForm = () => {
    setPromoForm({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      event_id: '',
      usage_limit: '',
      expiry_date: '',
      is_active: true
    });
    setEditingPromo(null);
  };

  // Wallpaper functions
  const handleWallpaperUpload = async (e, isMobile = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (isMobile) {
      setUploadingMobileWallpaper(true);
    } else {
      setUploadingWallpaper(true);
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', `${wallpaperForm.title || 'Untitled'}${isMobile ? '-mobile' : '-desktop'}`);
      
      const res = await axios.post(`${API}/login-settings/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        if (isMobile) {
          setWallpaperForm(prev => ({ ...prev, mobile_image_url: res.data.image_url }));
        } else {
          setWallpaperForm(prev => ({ ...prev, image_url: res.data.image_url }));
        }
      }
    } catch (err) {
      console.error('Error uploading wallpaper:', err);
      alert(err.response?.data?.detail || 'Failed to upload image. Please try again.');
    } finally {
      if (isMobile) {
        setUploadingMobileWallpaper(false);
      } else {
        setUploadingWallpaper(false);
      }
    }
  };

  const handleSaveWallpaper = async () => {
    if (!wallpaperForm.title || !wallpaperForm.image_url) return;
    
    setSavingWallpaper(true);
    try {
      const res = await axios.post(`${API}/login-settings/wallpapers`, {
        title: wallpaperForm.title,
        image_url: wallpaperForm.image_url,
        mobile_image_url: wallpaperForm.mobile_image_url || null,
        event_id: wallpaperForm.event_id || null
      });
      
      if (res.data.success) {
        setWallpapers(prev => [...prev, res.data.wallpaper]);
        setShowWallpaperModal(false);
        setWallpaperForm({ title: '', image_url: '', mobile_image_url: '', event_id: '' });
      }
    } catch (err) {
      console.error('Error saving wallpaper:', err);
      alert('Failed to save wallpaper. Please try again.');
    } finally {
      setSavingWallpaper(false);
    }
  };

  const activateWallpaper = async (wallpaperId) => {
    try {
      const res = await axios.put(`${API}/login-settings/wallpapers/${wallpaperId}/activate`);
      if (res.data.success) {
        // Update local state
        setWallpapers(prev => prev.map(w => ({
          ...w,
          is_active: w.id === wallpaperId
        })));
      }
    } catch (err) {
      console.error('Error activating wallpaper:', err);
      alert('Failed to activate wallpaper.');
    }
  };

  const deactivateAllWallpapers = async () => {
    try {
      const res = await axios.put(`${API}/login-settings/wallpapers/deactivate-all`);
      if (res.data.success) {
        setWallpapers(prev => prev.map(w => ({ ...w, is_active: false })));
      }
    } catch (err) {
      console.error('Error deactivating wallpapers:', err);
    }
  };

  const deleteWallpaper = async (wallpaperId) => {
    if (!confirm('Delete this wallpaper?')) return;
    
    try {
      const res = await axios.delete(`${API}/login-settings/wallpapers/${wallpaperId}`);
      if (res.data.success) {
        setWallpapers(prev => prev.filter(w => w.id !== wallpaperId));
      }
    } catch (err) {
      console.error('Error deleting wallpaper:', err);
      alert('Failed to delete wallpaper.');
    }
  };

  const handleSavePromo = async (e) => {
    e.preventDefault();
    setSavingPromo(true);
    
    try {
      const data = {
        code: promoForm.code.toUpperCase(),
        discount_type: promoForm.discount_type,
        discount_value: parseFloat(promoForm.discount_value),
        event_id: promoForm.event_id || null,
        usage_limit: promoForm.usage_limit ? parseInt(promoForm.usage_limit) : null,
        expiry_date: promoForm.expiry_date || null,
        is_active: promoForm.is_active
      };
      
      if (editingPromo) {
        await axios.put(`${API}/livestream/admin/promo-codes/${editingPromo.id}`, data, getAuthHeaders());
      } else {
        await axios.post(`${API}/livestream/admin/promo-codes`, data, getAuthHeaders());
      }
      
      loadPromoCodes();
      setShowPromoModal(false);
      resetPromoForm();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving promo code');
    } finally {
      setSavingPromo(false);
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/livestream/admin/promo-codes/${id}`, getAuthHeaders());
      loadPromoCodes();
    } catch (err) {
      alert('Error deleting promo code');
    }
  };

  const copyPromoLink = (promo) => {
    const event = events.find(e => e.id === promo.event_id);
    const slug = event ? event.slug : events[0]?.slug;
    if (!slug) {
      alert('No event available for promo link');
      return;
    }
    const link = `${window.location.origin}/live/event/${slug}?promo=${promo.code}`;
    navigator.clipboard.writeText(link);
    alert('Promo link copied to clipboard!');
  };

  // Moderator functions
  const loadModerators = async () => {
    try {
      const res = await axios.get(`${API}/livestream/admin/moderators`, getAuthHeaders());
      setModerators(res.data.moderators || []);
    } catch (err) {
      console.error('Error loading moderators:', err);
    }
  };

  const toggleModerator = async (customerId, isMod) => {
    try {
      if (isMod) {
        await axios.delete(`${API}/livestream/admin/moderators/${customerId}`, getAuthHeaders());
      } else {
        await axios.post(`${API}/livestream/admin/moderators/${customerId}`, {}, getAuthHeaders());
      }
      refreshCustomers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating moderator status');
    }
  };

  // Chat moderation functions
  const loadChatMessages = async (eventId) => {
    if (!eventId) return;
    setLoadingChat(true);
    try {
      const res = await axios.get(`${API}/livestream/admin/events/${eventId}/chat`, getAuthHeaders());
      const messages = Array.isArray(res.data?.messages) ? res.data.messages : [];
      setChatMessages(messages);
    } catch (err) {
      console.error('Error loading chat:', err);
      setChatMessages([]);
    } finally {
      setLoadingChat(false);
    }
  };

  const clearAllChat = async (eventId) => {
    if (!window.confirm('Are you sure you want to clear ALL chat messages for this event? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/livestream/admin/events/${eventId}/chat`, getAuthHeaders());
      setChatMessages([]);
      alert('All chat messages cleared');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error clearing chat');
    }
  };

  const deleteChatMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/livestream/admin/chat/message/${messageId}`, getAuthHeaders());
      setChatMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting message');
    }
  };

  // Cloudflare Stream functions
  const createLiveInput = async (eventId) => {
    setCreatingStream(true);
    try {
      const res = await axios.post(`${API}/cloudflare/live-inputs`, {
        event_id: eventId,
        recording_enabled: true
      }, getAuthHeaders());
      
      setStreamSetup(prev => ({ ...prev, [eventId]: res.data }));
      alert('Live input created! Copy the RTMPS URL and Stream Key to your OBS or streaming software.');
      refreshEvents();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating live input');
    } finally {
      setCreatingStream(false);
    }
  };

  const getStreamSetup = async (eventId) => {
    try {
      const res = await axios.get(`${API}/cloudflare/live-inputs/${eventId}`, getAuthHeaders());
      setStreamSetup(prev => ({ ...prev, [eventId]: res.data }));
    } catch (err) {
      console.error('Error getting stream setup:', err);
    }
  };

  // Enable signed URLs for an event
  const enableSignedUrls = async (eventId) => {
    setEnablingSignedUrls(true);
    try {
      await axios.post(`${API}/cloudflare/signing-keys`, { event_id: eventId }, getAuthHeaders());
      alert('Signed URLs enabled! All viewers will now get unique, time-limited access tokens.');
      refreshEvents();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error enabling signed URLs');
    } finally {
      setEnablingSignedUrls(false);
    }
  };

  // Disable signed URLs for an event
  const disableSignedUrls = async (eventId) => {
    if (!window.confirm('Disable signed URLs? This will allow anyone with the stream URL to watch.')) return;
    try {
      await axios.delete(`${API}/cloudflare/signing-keys/${eventId}`, getAuthHeaders());
      alert('Signed URLs disabled.');
      refreshEvents();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error disabling signed URLs');
    }
  };

  // Update expiration time
  const updateExpiration = async (eventId, minutes) => {
    setUpdatingExpiration(prev => ({ ...prev, [eventId]: true }));
    try {
      await axios.put(`${API}/cloudflare/signing-keys/${eventId}/expiration`, 
        { expiration_minutes: parseInt(minutes) }, 
        getAuthHeaders()
      );
      alert(`URL expiration updated to ${minutes} minutes`);
      refreshEvents();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating expiration');
    } finally {
      setUpdatingExpiration(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // Save domain restrictions
  const saveDomainRestriction = async (eventId) => {
    setSavingDomains(prev => ({ ...prev, [eventId]: true }));
    const domains = (domainInput[eventId] || '').split(',').map(d => d.trim()).filter(d => d);
    
    try {
      await axios.put(`${API}/cloudflare/domain-restriction`, 
        { event_id: eventId, allowed_domains: domains }, 
        getAuthHeaders()
      );
      alert(domains.length > 0 
        ? `Domain restriction enabled! Playback only allowed on: ${domains.join(', ')}` 
        : 'Domain restriction disabled. Playback allowed from any domain.');
      refreshEvents();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating domain restriction');
    } finally {
      setSavingDomains(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // Scorecard contestant functions
  const addContestantToScorecard = () => {
    if (!newContestantName.trim()) return;
    setScorecard({
      ...scorecard,
      contestants: [...scorecard.contestants, { name: newContestantName.trim(), score: 0 }]
    });
    setNewContestantName('');
  };

  const removeContestantFromScorecard = (index) => {
    setScorecard({
      ...scorecard,
      contestants: scorecard.contestants.filter((_, i) => i !== index)
    });
  };

  const updateContestantScore = (index, delta) => {
    const updated = [...scorecard.contestants];
    updated[index].score = Math.max(0, updated[index].score + delta);
    setScorecard({ ...scorecard, contestants: updated });
  };

  const updateContestantName = (index, name) => {
    const updated = [...scorecard.contestants];
    updated[index].name = name;
    setScorecard({ ...scorecard, contestants: updated });
  };

  const setBattleContestant = (side, contestant) => {
    if (side === 'left') {
      setScorecard({ ...scorecard, battle_left: contestant ? { ...contestant } : null });
    } else {
      setScorecard({ ...scorecard, battle_right: contestant ? { ...contestant } : null });
    }
  };

  const updateBattleScore = (side, delta) => {
    if (side === 'left' && scorecard.battle_left) {
      setScorecard({
        ...scorecard,
        battle_left: { ...scorecard.battle_left, score: Math.max(0, scorecard.battle_left.score + delta) }
      });
    } else if (side === 'right' && scorecard.battle_right) {
      setScorecard({
        ...scorecard,
        battle_right: { ...scorecard.battle_right, score: Math.max(0, scorecard.battle_right.score + delta) }
      });
    }
  };

  // Reset functions
  const resetForm = () => {
    setFormData({
      title: '', description: '', event_date: '', price: '', currency: 'GBP',
      thumbnail_url: '', mobile_thumbnail_url: '', social_share_image: '', promo_video_url: '', show_promo_video: false, stream_url: '', stripe_account: 'default',
      voting_enabled: false, voting_options: [], status: 'upcoming',
      // SEO fields
      seo_title: '', seo_description: '', seo_keywords: '', og_title: '', og_description: '',
      // Custom branding fields
      custom_logo_url: '', custom_signin_wallpaper_url: '', custom_signin_wallpaper_mobile_url: '', restricted_navigation: false, vod_video_id: ''
    });
    setVotingContestants([{ name: '', image: '', description: '' }, { name: '', image: '', description: '' }]);
    setEditingEvent(null);
    setCreateStep(1);
  };

  const resetCustomerForm = () => {
    setCustomerForm({ email: '', password: '', full_name: '', username: '' });
    setEditingCustomer(null);
  };

  // Event handlers
  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title, description: event.description,
      event_date: event.event_date.slice(0, 16), price: event.price.toString(),
      currency: event.currency, thumbnail_url: event.thumbnail_url || '',
      mobile_thumbnail_url: event.mobile_thumbnail_url || '',
      social_share_image: event.social_share_image || '',
      promo_video_url: event.promo_video_url || '',
      show_promo_video: event.show_promo_video || false,
      stream_url: event.stream_url || '', stripe_account: event.stripe_account || 'default',
      voting_enabled: event.voting_enabled || false, voting_options: event.voting_options || [],
      status: event.status,
      // SEO fields
      seo_title: event.seo_title || '',
      seo_description: event.seo_description || '',
      seo_keywords: event.seo_keywords || '',
      og_title: event.og_title || '',
      og_description: event.og_description || '',
      // Custom branding fields
      custom_logo_url: event.custom_logo_url || '',
      custom_signin_wallpaper_url: event.custom_signin_wallpaper_url || '',
      custom_signin_wallpaper_mobile_url: event.custom_signin_wallpaper_mobile_url || '',
      restricted_navigation: event.restricted_navigation || false,
      vod_video_id: event.vod_video_id || ''
    });
    
    if (event.voting_options?.length > 0) {
      const contestants = event.voting_options.map(opt => 
        typeof opt === 'string' ? { name: opt, image: '', description: '' } : opt
      );
      setVotingContestants(contestants);
    }
    setShowCreateModal(true);
    fetchVodVideos();
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingThumbnail(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      // Upload to Cloudflare Images
      const res = await axios.post(`${API}/cloudflare/images/upload-file`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { 
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      if (res.data.success && res.data.url) {
        setFormData({ ...formData, thumbnail_url: res.data.url });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload image to Cloudflare');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleMobileThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingMobileThumbnail(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      // Upload to Cloudflare Images
      const res = await axios.post(`${API}/cloudflare/images/upload-file`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { 
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      if (res.data.success && res.data.url) {
        setFormData({ ...formData, mobile_thumbnail_url: res.data.url });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Mobile thumbnail upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload mobile image to Cloudflare');
    } finally {
      setUploadingMobileThumbnail(false);
    }
  };

  const handleSocialImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingSocialImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API}/cloudflare/images/upload-file`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { 
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      if (res.data.success && res.data.url) {
        setFormData({ ...formData, social_share_image: res.data.url });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Social image upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload image to Cloudflare');
    } finally {
      setUploadingSocialImage(false);
    }
  };

  // Upload custom logo for event branding
  const handleCustomLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCustomLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API}/cloudflare/images/upload-file`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { 
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      if (res.data.success && res.data.url) {
        setFormData({ ...formData, custom_logo_url: res.data.url });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Custom logo upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload logo to Cloudflare');
    } finally {
      setUploadingCustomLogo(false);
    }
  };

  // Upload custom wallpaper for event sign-in page
  const handleCustomWallpaperUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCustomWallpaper(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API}/cloudflare/images/upload-file`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { 
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      if (res.data.success && res.data.url) {
        setFormData({ ...formData, custom_signin_wallpaper_url: res.data.url });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Custom wallpaper upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload wallpaper to Cloudflare');
    } finally {
      setUploadingCustomWallpaper(false);
    }
  };

  // Upload custom mobile wallpaper for event sign-in page (portrait 1080x1920)
  const handleCustomWallpaperMobileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCustomWallpaperMobile(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await axios.post(`${API}/cloudflare/images/upload-file`, formDataUpload, {
        ...getAuthHeaders(),
        headers: { 
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data' 
        }
      });
      
      if (res.data.success && res.data.url) {
        setFormData({ ...formData, custom_signin_wallpaper_mobile_url: res.data.url });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Custom mobile wallpaper upload error:', err);
      alert(err.response?.data?.detail || 'Failed to upload mobile wallpaper to Cloudflare');
    } finally {
      setUploadingCustomWallpaperMobile(false);
    }
  };

  const handlePromoVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please upload a video file (MP4, MOV, etc.)');
      return;
    }
    
    // Validate file size (max 1GB for promo videos)
    if (file.size > 1024 * 1024 * 1024) {
      alert('Video must be under 1GB');
      return;
    }
    
    setUploadingPromoVideo(true);
    setPromoVideoProgress(0);
    
    try {
      // Get a direct upload URL from Cloudflare
      const initRes = await axios.post(`${API}/cloudflare/stream/upload-url-json`, {
        name: `promo-${formData.title || 'event'}-${Date.now()}`,
        maxDurationSeconds: 900  // 15 minutes max for promo videos
      }, getAuthHeaders());
      
      if (!initRes.data.success || !initRes.data.uploadURL || !initRes.data.uid) {
        throw new Error('Failed to get upload URL from Cloudflare');
      }
      
      const uploadURL = initRes.data.uploadURL;
      const videoId = initRes.data.uid;
      
      // Use basic POST upload with FormData (not TUS)
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Upload using XMLHttpRequest for progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setPromoVideoProgress(progress);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });
        
        xhr.open('POST', uploadURL);
        xhr.send(formDataUpload);
      });
      
      // Upload complete - set the video URL
      const videoUrl = `https://iframe.videodelivery.net/${videoId}`;
      setFormData(prev => ({ ...prev, promo_video_url: videoUrl, show_promo_video: true }));
      
    } catch (err) {
      console.error('Promo video upload error:', err);
      alert('Failed to upload video to Cloudflare. You can also paste a Cloudflare Stream URL directly.');
    } finally {
      setUploadingPromoVideo(false);
      setPromoVideoProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const votingOptions = formData.voting_enabled 
      ? votingContestants.filter(c => c.name.trim()).map(c => c.name.trim())
      : [];

    const payload = { ...formData, price: parseFloat(formData.price), voting_options: votingOptions };

    try {
      if (editingEvent) {
        await axios.put(`${API}/livestream/admin/events/${editingEvent.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${API}/livestream/admin/events`, payload, getAuthHeaders());
      }
      setShowCreateModal(false);
      resetForm();
      refreshEvents();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event and all related data?')) return;
    try {
      await axios.delete(`${API}/livestream/admin/events/${eventId}`, getAuthHeaders());
      refreshEvents();
    } catch (err) {
      alert('Failed to delete event');
    }
  };

  const handleEndEvent = async (event) => {
    if (!window.confirm(`End event "${event.title}"? This will archive the stream credentials (RTMP URL & Stream Key) and mark the event as ended.`)) return;
    try {
      await axios.post(`${API}/livestream/admin/events/${event.id}/end`, {}, getAuthHeaders());
      refreshEvents();
    } catch (err) {
      alert('Failed to end event');
    }
  };

  // Customer handlers
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      email: customer.email, password: '', full_name: customer.full_name, username: customer.username || ''
    });
    setShowCustomerModal(true);
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCustomer) {
        const updateData = { ...customerForm };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API}/livestream/admin/customers/${editingCustomer.id}`, updateData, getAuthHeaders());
      } else {
        await axios.post(`${API}/livestream/admin/customers`, customerForm, getAuthHeaders());
      }
      setShowCustomerModal(false);
      resetCustomerForm();
      refreshCustomers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer and all their data (tickets, votes, messages)?')) return;
    try {
      await axios.delete(`${API}/livestream/admin/customers/${customerId}`, getAuthHeaders());
      refreshCustomers();
    } catch (err) {
      alert('Failed to delete customer');
    }
  };

  // View customer ticket details
  const handleViewCustomerTickets = async (customer) => {
    setSelectedCustomerDetail(customer);
    setLoadingCustomerTickets(true);
    setShowCustomerDetailModal(true);
    
    try {
      const res = await axios.get(`${API}/livestream/admin/tickets/customer/${customer.id}`, getAuthHeaders());
      setCustomerTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Error loading customer tickets:', err);
      setCustomerTickets([]);
    } finally {
      setLoadingCustomerTickets(false);
    }
  };

  // Grant ticket to customer
  const handleGrantTicket = async (eventId) => {
    if (!selectedCustomerDetail) return;
    
    try {
      const res = await axios.post(`${API}/livestream/admin/tickets/grant`, {
        customer_id: selectedCustomerDetail.id,
        event_id: eventId,
        note: 'Admin granted ticket'
      }, getAuthHeaders());
      
      if (res.data.success) {
        // Refresh tickets
        handleViewCustomerTickets(selectedCustomerDetail);
        setShowGrantTicketModal(false);
        refreshCustomers();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to grant ticket');
    }
  };

  // Revoke ticket from customer
  const handleRevokeTicket = async (ticketId, eventTitle) => {
    if (!window.confirm(`Remove ticket for "${eventTitle}" from this customer?`)) return;
    
    try {
      await axios.delete(`${API}/livestream/admin/tickets/${ticketId}`, getAuthHeaders());
      // Refresh tickets
      handleViewCustomerTickets(selectedCustomerDetail);
      refreshCustomers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke ticket');
    }
  };

  // Bulk selection handlers
  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = (customerList) => {
    if (selectedCustomers.length === customerList.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customerList.map(c => c.id));
    }
  };

  // Light refresh - only reload customers (for bulk actions)
  const refreshCustomers = async () => {
    try {
      const [customersRes, modsRes] = await Promise.all([
        axios.get(`${API}/livestream/admin/customers`, getAuthHeaders()),
        axios.get(`${API}/livestream/admin/moderators`, getAuthHeaders()).catch(() => ({ data: { moderators: [] } }))
      ]);
      setCustomers(customersRes.data.customers || []);
      setModerators(modsRes.data.moderators || []);
    } catch (err) {
      console.error('Error refreshing customers:', err);
    }
  };

  // Light refresh - only reload events
  const refreshEvents = async () => {
    try {
      const eventsRes = await axios.get(`${API}/livestream/admin/events`, getAuthHeaders());
      setEvents(eventsRes.data.events || []);
    } catch (err) {
      console.error('Error refreshing events:', err);
    }
  };

  // Light refresh - only reload promo codes
  const refreshPromoCodes = async () => {
    try {
      const promoRes = await axios.get(`${API}/livestream/admin/promo-codes`, getAuthHeaders());
      setPromoCodes(promoRes.data.promo_codes || []);
    } catch (err) {
      console.error('Error refreshing promo codes:', err);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedCustomers.length === 0) {
      alert('Please select customers and an action first');
      return;
    }
    
    const actionLabels = {
      'delete': 'Delete',
      'make_moderator': 'Make Moderator',
      'remove_moderator': 'Remove Moderator',
      'make_admin': 'Make Admin',
      'remove_admin': 'Remove Admin'
    };
    
    const confirmMsg = `${actionLabels[bulkAction]} ${selectedCustomers.length} customer(s)?${bulkAction === 'delete' ? '\n\nThis will permanently delete all their data (tickets, votes, messages).' : ''}`;
    
    if (!window.confirm(confirmMsg)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const customerId of selectedCustomers) {
        try {
          if (bulkAction === 'delete') {
            await axios.delete(`${API}/livestream/admin/customers/${customerId}`, getAuthHeaders());
          } else if (bulkAction === 'make_moderator') {
            await axios.post(`${API}/livestream/admin/moderators/${customerId}`, {}, getAuthHeaders());
          } else if (bulkAction === 'remove_moderator') {
            await axios.delete(`${API}/livestream/admin/moderators/${customerId}`, getAuthHeaders());
          } else if (bulkAction === 'make_admin') {
            await axios.post(`${API}/livestream/admin/admins/${customerId}`, {}, getAuthHeaders());
          } else if (bulkAction === 'remove_admin') {
            await axios.delete(`${API}/livestream/admin/admins/${customerId}`, getAuthHeaders());
          }
          successCount++;
        } catch (err) {
          console.error(`Failed for customer ${customerId}:`, err);
          failCount++;
        }
      }
      
      setSelectedCustomers([]);
      setBulkAction('');
      
      if (failCount > 0) {
        alert(`Completed: ${successCount} successful, ${failCount} failed`);
      } else {
        alert(`Successfully processed ${successCount} customer(s)`);
      }
      
      // Only refresh customers, not everything
      refreshCustomers();
    } catch (err) {
      console.error('Bulk action error:', err);
      alert('Bulk action failed. Please try again.');
      refreshCustomers();
    }
  };

  // Toggle admin status
  const toggleAdmin = async (customerId, isCurrentlyAdmin) => {
    try {
      if (isCurrentlyAdmin) {
        await axios.delete(`${API}/livestream/admin/admins/${customerId}`, getAuthHeaders());
      } else {
        await axios.post(`${API}/livestream/admin/admins/${customerId}`, {}, getAuthHeaders());
      }
      refreshCustomers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update admin status');
    }
  };

  // Voting question handlers
  const handleSaveQuestion = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        question: questionForm.question,
        options: questionForm.options.filter(o => o.trim()),
        is_active: questionForm.is_active
      };
      
      if (editingQuestion) {
        await axios.put(`${API}/livestream/admin/voting/questions/${editingQuestion.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${API}/livestream/admin/voting/${selectedEvent.id}/questions`, payload, getAuthHeaders());
      }
      setShowQuestionModal(false);
      setEditingQuestion(null);
      setQuestionForm({ question: '', options: ['', ''], is_active: true });
      loadVotingQuestions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question and all votes?')) return;
    try {
      await axios.delete(`${API}/livestream/admin/voting/questions/${questionId}`, getAuthHeaders());
      loadVotingQuestions();
    } catch (err) {
      alert('Failed to delete question');
    }
  };

  const handleResetVotes = async (questionId) => {
    if (!window.confirm('Reset all votes for this question?')) return;
    try {
      await axios.post(`${API}/livestream/admin/voting/questions/${questionId}/reset`, {}, getAuthHeaders());
      loadVotingQuestions();
      // Clear cached results
      setVotingResults(prev => {
        const updated = {...prev};
        delete updated[questionId];
        return updated;
      });
    } catch (err) {
      alert('Failed to reset votes');
    }
  };

  // Load voting results for a specific question (admin only view)
  const loadVotingResults = async (questionId) => {
    setLoadingVotingResults(true);
    try {
      const response = await axios.get(`${API}/livestream/admin/voting/questions/${questionId}/results`, getAuthHeaders());
      setVotingResults(prev => ({...prev, [questionId]: response.data}));
    } catch (err) {
      console.error('Failed to load voting results:', err);
    } finally {
      setLoadingVotingResults(false);
    }
  };

  // Announce winner
  const announceWinner = async (questionId, winner, showResults = true) => {
    setAnnouncingWinner(true);
    try {
      await axios.post(`${API}/livestream/admin/voting/questions/${questionId}/announce-winner`, 
        { winner, show_results: showResults }, 
        getAuthHeaders()
      );
      loadVotingQuestions();
      loadVotingResults(questionId);
      alert(`🏆 Winner announced: ${winner}`);
    } catch (err) {
      alert('Failed to announce winner');
    } finally {
      setAnnouncingWinner(false);
    }
  };

  // Hide voting results
  const hideVotingResults = async (questionId) => {
    try {
      await axios.post(`${API}/livestream/admin/voting/questions/${questionId}/hide-results`, {}, getAuthHeaders());
      loadVotingQuestions();
      setVotingResults(prev => {
        const updated = {...prev};
        delete updated[questionId];
        return updated;
      });
    } catch (err) {
      alert('Failed to hide results');
    }
  };

  // Scorecard handlers
  const handleSaveScorecard = async () => {
    setSavingScorecard(true);
    try {
      await axios.put(`${API}/livestream/admin/scorecard/${selectedEvent.id}`, scorecard, getAuthHeaders());
    } catch (err) {
      alert('Failed to save scorecard');
    } finally {
      setSavingScorecard(false);
    }
  };

  // Contestant handlers for event creation
  const addContestant = () => setVotingContestants([...votingContestants, { name: '', image: '', description: '' }]);
  const removeContestant = (idx) => votingContestants.length > 2 && setVotingContestants(votingContestants.filter((_, i) => i !== idx));
  const updateContestant = (idx, field, value) => {
    const updated = [...votingContestants];
    updated[idx][field] = value;
    setVotingContestants(updated);
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const getStatusColor = (status) => ({ live: 'bg-red-500', upcoming: 'bg-blue-500' }[status] || 'bg-gray-500');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[128px]" />
      </div>

      {/* Header */}
      <header className="relative z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white/70 hover:text-white" onClick={() => navigate('/live')}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back to Live
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src="/ltv-live-logo.png" 
                alt="LTV Live" 
                className="w-10 h-10 object-contain brightness-0 invert"
              />
              <div>
                <h1 className="text-xl font-bold">LTV Live</h1>
                <p className="text-xs text-gray-500">Livestream Manager</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
              onClick={() => setShowResetConfirm(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />Reset Data
            </Button>
            <Button className="bg-gradient-to-r from-red-600 to-red-500" onClick={() => { resetForm(); setShowCreateModal(true); fetchVodVideos(); }}>
              <Plus className="h-4 w-4 mr-2" />Create Event
            </Button>
          </div>
        </div>
      </header>

      {/* Reset Data Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-[#1a1a1a] border border-red-600 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-red-500 mb-4 flex items-center gap-2">
              <AlertCircle className="h-6 w-6" />
              Reset All Data?
            </h3>
            <p className="text-gray-300 mb-4">
              This will permanently delete:
            </p>
            <ul className="text-gray-400 mb-6 space-y-1 text-sm">
              <li>• All ticket purchases & revenue</li>
              <li>• All live chat messages</li>
              <li>• All viewer analytics</li>
              <li>• All polls and votes</li>
              <li>• Event statistics will be reset to zero</li>
            </ul>
            <p className="text-red-400 text-sm mb-6 font-medium">
              ⚠️ This action cannot be undone!
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-gray-600"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleResetAllData}
                disabled={resettingData}
              >
                {resettingData ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Yes, Reset All
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center">
                <Radio className="h-5 w-5 text-white" />
              </div>
              LTV Live Dashboard
            </h2>
            <p className="text-gray-400 text-sm mt-2">Manage livestream events, analytics & revenue</p>
          </div>
        </div>

        {/* Stats - Traffic Source Style */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <StatCard icon={Video} label="Total Events" value={stats.total_events} color="gray" subtext="All time" />
            <StatCard icon={Calendar} label="Upcoming" value={stats.upcoming_events} color="blue" subtext="Scheduled" />
            <StatCard icon={Radio} label="Live Now" value={stats.live_now} color="red" pulse subtext="Broadcasting" />
            <StatCard icon={Ticket} label="Tickets Sold" value={stats.total_tickets} color="cyan" subtext="All events" />
            <StatCard icon={DollarSign} label="Gross Revenue" value={`£${stats.gross_revenue?.toFixed(2) || '0.00'}`} color="yellow" subtext="Before fees" />
            <StatCard icon={DollarSign} label="Net Revenue" value={`£${stats.net_revenue?.toFixed(2) || '0.00'}`} color="green" subtext="After fees" />
            <StatCard icon={UserCircle} label="Customers" value={stats.total_customers} color="purple" subtext="Registered" />
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value)} className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10 p-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1 h-auto">
            <TabsTrigger value="events" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Video className="h-4 w-4" />
              <span>Events</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <BarChart3 className="h-4 w-4" />
              <span>Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="purchases" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <CreditCard className="h-4 w-4" />
              <span>Purchases</span>
            </TabsTrigger>
            <TabsTrigger value="streaming" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Tv className="h-4 w-4" />
              <span>Streaming</span>
            </TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Users className="h-4 w-4" />
              <span>Customers</span>
            </TabsTrigger>
            <TabsTrigger value="chat" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <MessageSquare className="h-4 w-4" />
              <span>Chat</span>
            </TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Tag className="h-4 w-4" />
              <span>Promo</span>
            </TabsTrigger>
            <TabsTrigger value="viewers" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Eye className="h-4 w-4" />
              <span>Viewers</span>
            </TabsTrigger>
            <TabsTrigger value="voting" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <HelpCircle className="h-4 w-4" />
              <span>Voting</span>
            </TabsTrigger>
            <TabsTrigger value="scorecard" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Target className="h-4 w-4" />
              <span>Scorecard</span>
            </TabsTrigger>
            <TabsTrigger value="wallpapers" className="data-[state=active]:bg-white data-[state=active]:text-black transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Image className="h-4 w-4" />
              <span>Wallpapers</span>
            </TabsTrigger>
            <TabsTrigger value="crosspromo" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-pink-500 data-[state=active]:text-white transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1">
              <Megaphone className="h-4 w-4" />
              <span>Promos</span>
            </TabsTrigger>
            <TabsTrigger value="seo" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="seo-tab-trigger">
              <SearchCode className="h-4 w-4" />
              <span>SEO</span>
            </TabsTrigger>
            <TabsTrigger value="stripe" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-500 data-[state=active]:text-white transition-none text-xs sm:text-sm px-2 py-2 flex flex-col sm:flex-row items-center justify-center gap-1" data-testid="stripe-tab-trigger">
              <CreditCard className="h-4 w-4" />
              <span>Stripe</span>
            </TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            {events.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-16 text-center">
                  <Video className="h-16 w-16 mx-auto mb-4 text-white/20" />
                  <h3 className="text-xl font-bold mb-2 text-white">No Events Yet</h3>
                  <Button className="bg-red-600" onClick={() => { resetForm(); setShowCreateModal(true); fetchVodVideos(); }}>
                    <Plus className="h-4 w-4 mr-2" />Create Event
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} onEdit={() => handleEdit(event)} onDelete={() => handleDelete(event.id)}
                    onSelect={() => setSelectedEvent(event)} formatDate={formatDate} getStatusColor={getStatusColor}
                    onSetupStream={createLiveInput} streamSetup={streamSetup[event.id]} creatingStream={creatingStream}
                    onOpenControlPanel={(evt) => { setControlPanelEvent(evt); setShowStreamControlPanel(true); }}
                    onEndEvent={handleEndEvent} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Analytics Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  Livestream Analytics
                </h2>
                <p className="text-gray-400 text-sm mt-1">Track performance across all your events</p>
              </div>
              <Button onClick={() => { fetchAnalyticsSummary(); if(selectedEvent) fetchAnalytics(selectedEvent.id); }} variant="outline" className="border-gray-700 hover:border-green-500">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>

            {/* Analytics Summary - Traffic Source Style */}
            {analyticsSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Video className="h-5 w-5 text-blue-400" />
                    <span className="text-gray-300 text-xs">Total Events</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{analyticsSummary.total_events}</p>
                  <p className="text-xs text-blue-300 mt-1">All time</p>
                </div>
                <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-green-400" />
                    <span className="text-gray-300 text-xs">Total Revenue</span>
                  </div>
                  <p className="text-2xl font-bold text-white">£{analyticsSummary.total_revenue?.toFixed(2)}</p>
                  <p className="text-xs text-green-300 mt-1">Lifetime earnings</p>
                </div>
                <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-purple-400" />
                    <span className="text-gray-300 text-xs">Total Viewers</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{analyticsSummary.total_viewers}</p>
                  <p className="text-xs text-purple-300 mt-1">Unique viewers</p>
                </div>
                <div className="bg-gradient-to-br from-orange-600/30 to-yellow-600/20 rounded-xl p-4 border border-orange-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-5 w-5 text-orange-400" />
                    <span className="text-gray-300 text-xs">Total Comments</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{analyticsSummary.total_comments}</p>
                  <p className="text-xs text-orange-300 mt-1">Chat messages</p>
                </div>
                <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-4 border border-red-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Ticket className="h-5 w-5 text-red-400" />
                    <span className="text-gray-300 text-xs">Tickets Sold</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{analyticsSummary.total_tickets_sold}</p>
                  <p className="text-xs text-red-300 mt-1">All events</p>
                </div>
              </div>
            )}

            {/* Event Selector */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h3 className="font-bold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-green-400" />
                  Event Analytics
                </h3>
                <p className="text-gray-400 text-sm mt-1">Select an event to view detailed analytics</p>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-6">
                  {events.map((event) => {
                    const isPast = event.status !== 'live' && event.event_date && new Date(event.event_date).setHours(23,59,59,999) < Date.now();
                    return (
                    <Button
                      key={event.id}
                      size="sm"
                      data-testid={`analytics-event-btn-${event.id}`}
                      className={selectedEvent?.id === event.id 
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white border-0" 
                        : isPast ? "bg-gray-800/60 text-gray-500 hover:bg-gray-700 border border-gray-700/50" : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"}
                      onClick={() => setSelectedEvent(event)}
                    >
                      {event.title}{isPast ? ' (Past)' : ''}
                    </Button>
                    );
                  })}
                </div>

                {loadingAnalytics ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-green-500" />
                    <p className="text-gray-400">Loading analytics...</p>
                  </div>
                ) : analyticsData ? (
                  <div className="space-y-6">
                    {/* Key Metrics - Traffic Source Style */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/10 rounded-xl p-4 border border-blue-500/20">
                        <Eye className="h-6 w-6 mb-2 text-blue-400" />
                        <p className="text-2xl font-bold text-white">{analyticsData.analytics?.total_viewers || 0}</p>
                        <p className="text-xs text-gray-400">Total Viewers</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/10 rounded-xl p-4 border border-purple-500/20">
                        <Users className="h-6 w-6 mb-2 text-purple-400" />
                        <p className="text-2xl font-bold text-white">{analyticsData.analytics?.peak_concurrent_viewers || 0}</p>
                        <p className="text-xs text-gray-400">Peak Concurrent</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-600/20 to-yellow-600/10 rounded-xl p-4 border border-orange-500/20">
                        <MessageSquare className="h-6 w-6 mb-2 text-orange-400" />
                        <p className="text-2xl font-bold text-white">{analyticsData.analytics?.total_comments || 0}</p>
                        <p className="text-xs text-gray-400">Total Comments</p>
                      </div>
                      <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/10 rounded-xl p-4 border border-yellow-500/20">
                        <Clock className="h-6 w-6 mb-2 text-yellow-400" />
                        <p className="text-2xl font-bold text-white">{analyticsData.analytics?.watch_hours || 0}</p>
                        <p className="text-xs text-gray-400">Watch Hours</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-600/20 to-rose-600/10 rounded-xl p-4 border border-red-500/20">
                        <Ticket className="h-6 w-6 mb-2 text-red-400" />
                        <p className="text-2xl font-bold text-white">{analyticsData.analytics?.tickets_sold || 0}</p>
                        <p className="text-xs text-gray-400">Tickets Sold</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/10 rounded-xl p-4 border border-green-500/20">
                        <DollarSign className="h-6 w-6 mb-2 text-green-400" />
                        <p className="text-2xl font-bold text-white">£{analyticsData.analytics?.total_revenue?.toFixed(2) || '0.00'}</p>
                        <p className="text-xs text-gray-400">Revenue</p>
                      </div>
                    </div>

                    {/* Device Breakdown - Traffic Source Style */}
                    {analyticsData.analytics?.device_breakdown && (
                      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                        <div className="p-4 border-b border-gray-800">
                          <h4 className="font-bold flex items-center gap-2">
                            <Monitor className="h-5 w-5 text-cyan-400" />
                            Device Breakdown
                          </h4>
                        </div>
                        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                          {[
                            { label: 'Desktop', value: analyticsData.analytics.device_breakdown.desktop || 0, icon: Monitor, color: 'blue' },
                            { label: 'Mobile', value: analyticsData.analytics.device_breakdown.mobile || 0, icon: Smartphone, color: 'green' },
                            { label: 'Tablet', value: analyticsData.analytics.device_breakdown.tablet || 0, icon: Tv, color: 'purple' },
                            { label: 'Unknown', value: analyticsData.analytics.device_breakdown.unknown || 0, icon: HelpCircle, color: 'gray' }
                          ].map((item) => {
                            const total = (analyticsData.analytics.device_breakdown.desktop || 0) + 
                                         (analyticsData.analytics.device_breakdown.mobile || 0) + 
                                         (analyticsData.analytics.device_breakdown.tablet || 0) + 
                                         (analyticsData.analytics.device_breakdown.unknown || 0);
                            const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                            return (
                              <div key={item.label}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <item.icon className={`h-4 w-4 text-${item.color}-400`} />
                                    <span className="text-sm text-gray-300">{item.label}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-white">{item.value}</span>
                                    <span className="text-xs text-gray-400">({pct}%)</span>
                                  </div>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full bg-${item.color}-500 rounded-full transition-all`}
                                    style={{ width: `${Math.max(parseFloat(pct), 2)}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <BarChart3 className="h-8 w-8 text-gray-600" />
                    </div>
                    <p className="text-gray-400">Select an event to view analytics</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases" className="space-y-6">
            {/* Purchases Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-white" />
                  </div>
                  Purchases & Revenue
                </h2>
                <p className="text-gray-400 text-sm mt-1">Track purchases, refunds & Stripe balance</p>
              </div>
              <Button onClick={() => { fetchStripeBalance(); if(selectedEvent) fetchPurchases(selectedEvent.id); }} variant="outline" className="border-gray-700 hover:border-purple-500">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>

            {/* Stripe Balance Overview - Traffic Source Style */}
            {stripeBalance?.error && (
              <div className="bg-yellow-900/30 border border-yellow-600/40 rounded-lg p-3 flex items-center gap-2 text-yellow-300 text-sm" data-testid="stripe-balance-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>Stripe balance unavailable: {stripeBalance.error}</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-5 border border-green-500/30" data-testid="stripe-available-balance">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-5 w-5 text-green-400" />
                  <span className="text-gray-300 text-xs">Available Balance</span>
                </div>
                <p className="text-3xl font-bold text-white">{stripeBalance?.currency === 'USD' ? '$' : '£'}{stripeBalance?.available?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-green-300 mt-1">Ready to payout</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-5 border border-yellow-500/30" data-testid="stripe-pending-balance">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-yellow-400" />
                  <span className="text-gray-300 text-xs">Pending Balance</span>
                </div>
                <p className="text-3xl font-bold text-white">{stripeBalance?.currency === 'USD' ? '$' : '£'}{stripeBalance?.pending?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-yellow-300 mt-1">Processing</p>
              </div>
              <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-5 border border-red-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-5 w-5 text-red-400" />
                  <span className="text-gray-300 text-xs">Total Stripe Fees</span>
                </div>
                <p className="text-3xl font-bold text-white">£{purchasesData?.financial_summary?.total_stripe_fees?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-red-300 mt-1">Processing fees</p>
              </div>
              <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-5 border border-purple-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-5 w-5 text-purple-400" />
                  <span className="text-gray-300 text-xs">Net Revenue</span>
                </div>
                <p className="text-3xl font-bold text-white">£{purchasesData?.financial_summary?.total_net_revenue?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-purple-300 mt-1">After fees</p>
              </div>
            </div>

            {/* Event Selector */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800">
                <h3 className="font-bold flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-400" />
                  Purchase History
                </h3>
                <p className="text-gray-400 text-sm mt-1">View all purchases and issue refunds</p>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-6">
                  {events.map((event) => {
                    const isPast = event.status !== 'live' && event.event_date && new Date(event.event_date).setHours(23,59,59,999) < Date.now();
                    return (
                    <Button
                      key={event.id}
                      size="sm"
                      data-testid={`purchases-event-btn-${event.id}`}
                      className={selectedEvent?.id === event.id 
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0" 
                        : isPast ? "bg-gray-800/60 text-gray-500 hover:bg-gray-700 border border-gray-700/50" : "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"}
                      onClick={() => setSelectedEvent(event)}
                    >
                      {event.title}{isPast ? ' (Past)' : ''}
                    </Button>
                    );
                  })}
                </div>

                {loadingPurchases ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-500" />
                    <p className="text-gray-400">Loading purchases...</p>
                  </div>
                ) : purchasesData ? (
                  <div className="space-y-6">
                    {/* Financial Summary - Traffic Source Style */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-gray-700/30 to-gray-800/20 rounded-xl p-4 border border-gray-600/30">
                        <p className="text-xs text-gray-400 mb-1">Gross Revenue</p>
                        <p className="text-xl font-bold text-white">£{purchasesData.financial_summary?.total_gross_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="bg-gradient-to-br from-red-600/20 to-rose-600/10 rounded-xl p-4 border border-red-500/20">
                        <p className="text-xs text-gray-400 mb-1">Stripe Fees</p>
                        <p className="text-xl font-bold text-red-400">-£{purchasesData.financial_summary?.total_stripe_fees?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/10 rounded-xl p-4 border border-green-500/20">
                        <p className="text-xs text-gray-400 mb-1">Net Revenue</p>
                        <p className="text-xl font-bold text-green-400">£{purchasesData.financial_summary?.total_net_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/10 rounded-xl p-4 border border-purple-500/20">
                        <p className="text-xs text-gray-400 mb-1">Total Purchases</p>
                        <p className="text-xl font-bold text-purple-400">{purchasesData.financial_summary?.total_purchases || 0}</p>
                      </div>
                    </div>

                    {/* Purchases Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-800/50">
                          <tr>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Customer</th>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Transaction ID</th>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Amount</th>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Stripe Fee</th>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Net</th>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Date</th>
                            <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Receipt</th>
                            <th className="text-right text-xs text-gray-400 font-medium px-4 py-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {purchasesData.purchases?.map((purchase) => (
                            <tr key={purchase.ticket_id} className="hover:bg-gray-800/50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                                    {(purchase.customer_name || '?')[0].toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-white">{purchase.customer_name}</p>
                                    <p className="text-xs text-gray-500">{purchase.customer_email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <code className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded block max-w-[120px] truncate" title={purchase.transaction_id}>
                                  {purchase.transaction_id}
                                </code>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-bold text-green-400">£{purchase.amount_paid?.toFixed(2)}</p>
                                {purchase.promo_code_used && (
                                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs mt-1">
                                    {purchase.promo_code_used}
                                  </Badge>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-medium text-red-400">-£{purchase.stripe_data?.fee?.toFixed(2) || '0.00'}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-bold text-blue-400">£{purchase.stripe_data?.net?.toFixed(2) || '0.00'}</p>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-400">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3.5 w-3.5 text-gray-500" />
                                  {new Date(purchase.purchased_at).toLocaleDateString('en-GB', {
                                    day: '2-digit', month: 'short', year: 'numeric'
                                  })}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {purchase.stripe_data?.receipt_url ? (
                                  <a
                                    href={purchase.stripe_data.receipt_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                                  >
                                    <ExternalLink className="h-3 w-3" /> View
                                  </a>
                                ) : (
                                  <span className="text-gray-500 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {purchase.refunded || purchase.stripe_data?.refunded ? (
                                  <Badge className="bg-gray-600/50 text-gray-400">Refunded</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                                    onClick={() => handleRefund(purchase.ticket_id)}
                                    disabled={processingRefund === purchase.ticket_id}
                                  >
                                    {processingRefund === purchase.ticket_id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      'Refund'
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {purchasesData.purchases?.length === 0 && (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                          <CreditCard className="h-8 w-8 text-gray-600" />
                        </div>
                        <p className="text-gray-400">No purchases yet for this event</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="h-8 w-8 text-gray-600" />
                    </div>
                    <p className="text-gray-400">Select an event to view purchases</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Streaming Tab */}
          <TabsContent value="streaming">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Tv className="h-5 w-5 text-orange-400" />
                  Streaming Credentials & Security
                </CardTitle>
                <CardDescription>
                  Your RTMPS URLs and Stream Keys for OBS. These credentials are <strong className="text-green-400">permanent</strong> - they don't change every time you go live.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {events.filter(e => e.cloudflare_live_input_id).length === 0 ? (
                  <div className="text-center py-12">
                    <Cloud className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                    <h3 className="text-xl font-bold text-white mb-2">No Streams Configured</h3>
                    <p className="text-gray-400 mb-4">Click the <Cloud className="h-4 w-4 inline text-orange-400" /> icon on any event in the Events tab to set up streaming.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {events.filter(e => e.cloudflare_live_input_id).map((event) => (
                      <div key={event.id} className="p-6 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {event.thumbnail_url ? (
                              <img src={event.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                                <Tv className="h-6 w-6 text-white" />
                              </div>
                            )}
                            <div>
                              <h3 className="font-bold text-white">{event.title}</h3>
                              <p className="text-sm text-gray-400">{new Date(event.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-600">Stream Ready</Badge>
                            {event.signed_urls_enabled && (
                              <Badge className="bg-purple-600"><Lock className="h-3 w-3 mr-1" />Secured</Badge>
                            )}
                            {event.domain_restriction_enabled && (
                              <Badge className="bg-cyan-600"><Globe className="h-3 w-3 mr-1" />Domain Lock</Badge>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* RTMPS URL */}
                          <div className="bg-black/30 rounded-xl p-4 border border-orange-500/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-orange-400 font-bold text-sm">RTMPS URL (Server)</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-8 text-orange-400 hover:text-orange-300"
                                onClick={() => {
                                  navigator.clipboard.writeText(event.cloudflare_rtmps_url || 'rtmps://live.cloudflare.com:443/live/');
                                  alert('RTMPS URL copied!');
                                }}
                              >
                                <Copy className="h-4 w-4 mr-1" />Copy
                              </Button>
                            </div>
                            <code className="text-white text-sm font-mono break-all block bg-black/50 p-3 rounded-lg">
                              {event.cloudflare_rtmps_url || 'rtmps://live.cloudflare.com:443/live/'}
                            </code>
                          </div>

                          {/* Stream Key */}
                          <div className="bg-black/30 rounded-xl p-4 border border-purple-500/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-purple-400 font-bold text-sm">Stream Key</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-8 text-purple-400 hover:text-purple-300"
                                onClick={() => {
                                  navigator.clipboard.writeText(event.cloudflare_stream_key);
                                  alert('Stream Key copied!');
                                }}
                              >
                                <Copy className="h-4 w-4 mr-1" />Copy
                              </Button>
                            </div>
                            <code className="text-white text-sm font-mono break-all block bg-black/50 p-3 rounded-lg">
                              {event.cloudflare_stream_key || 'No stream key - create stream first'}
                            </code>
                          </div>
                        </div>

                        {/* Stream Control Panel Button */}
                        <div className="mt-4">
                          <Button 
                            onClick={() => {
                              setControlPanelEvent(event);
                              setShowStreamControlPanel(true);
                            }}
                            className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white py-3"
                          >
                            <Radio className="h-5 w-5 mr-2" />
                            Open Stream Control Panel
                            <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded">YouTube Studio-like</span>
                          </Button>
                          <p className="text-xs text-gray-400 mt-2 text-center">
                            Preview stream, view analytics, monitor health, and moderate live chat
                          </p>
                        </div>

                        {/* Security Settings */}
                        <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Key className="h-5 w-5 text-purple-400" />
                              <span className="text-purple-400 font-bold">Playback Security (Signed URLs)</span>
                            </div>
                            {event.signed_urls_enabled ? (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-red-500/30 text-red-400"
                                onClick={() => disableSignedUrls(event.id)}
                              >
                                <Unlock className="h-4 w-4 mr-1" />Disable
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                className="bg-purple-600 hover:bg-purple-700"
                                onClick={() => enableSignedUrls(event.id)}
                                disabled={enablingSignedUrls}
                              >
                                {enablingSignedUrls ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
                                Enable Security
                              </Button>
                            )}
                          </div>
                          
                          {event.signed_urls_enabled ? (
                            <div className="space-y-3">
                              <p className="text-sm text-green-400">
                                ✓ Signed URLs are <strong>ENABLED</strong>. Each viewer gets a unique, time-limited token. Shared links won't work for others.
                              </p>
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-400">URL expires after:</span>
                                <select
                                  className="bg-black/50 border border-purple-500/50 rounded-lg px-3 py-2 text-white text-sm"
                                  value={event.signed_url_expiration_minutes || 60}
                                  onChange={(e) => updateExpiration(event.id, e.target.value)}
                                  disabled={updatingExpiration[event.id]}
                                >
                                  <option value="15">15 minutes</option>
                                  <option value="30">30 minutes</option>
                                  <option value="60">1 hour</option>
                                  <option value="120">2 hours</option>
                                  <option value="180">3 hours</option>
                                  <option value="240">4 hours</option>
                                  <option value="360">6 hours</option>
                                  <option value="720">12 hours</option>
                                  <option value="1440">24 hours</option>
                                </select>
                                {updatingExpiration[event.id] && <Loader2 className="h-4 w-4 animate-spin text-purple-400" />}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">
                              Enable signed URLs to prevent unauthorized sharing. Each viewer will get a unique access token that expires after your set time.
                            </p>
                          )}
                        </div>

                        {/* Domain Restriction */}
                        <div className="mt-4 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Globe className="h-5 w-5 text-cyan-400" />
                              <span className="text-cyan-400 font-bold">Domain Restriction</span>
                              {event.domain_restriction_enabled && (
                                <Badge className="bg-cyan-600 text-xs">Active</Badge>
                              )}
                            </div>
                          </div>
                          
                          <p className="text-sm text-gray-400 mb-3">
                            {event.domain_restriction_enabled 
                              ? `Playback only allowed on: ${(event.allowed_domains || []).join(', ')}`
                              : 'Restrict playback to only your website domain(s). This prevents the video from being embedded on other sites.'}
                          </p>
                          
                          <div className="flex gap-2">
                            <Input
                              placeholder="e.g., ltvproduction.com, www.ltvproduction.com"
                              value={domainInput[event.id] !== undefined ? domainInput[event.id] : (event.allowed_domains || []).join(', ')}
                              onChange={(e) => setDomainInput(prev => ({ ...prev, [event.id]: e.target.value }))}
                              className="bg-black/50 border-cyan-500/50 text-white text-sm flex-1"
                            />
                            <Button
                              size="sm"
                              className="bg-cyan-600 hover:bg-cyan-700"
                              onClick={() => saveDomainRestriction(event.id)}
                              disabled={savingDomains[event.id]}
                            >
                              {savingDomains[event.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Separate multiple domains with commas. Leave empty to allow playback from any domain.
                          </p>
                        </div>

                        {/* OBS Instructions */}
                        <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                          <h4 className="text-blue-400 font-bold text-sm mb-2">How to Stream with OBS:</h4>
                          <ol className="text-sm text-gray-300 space-y-1">
                            <li>1. Open OBS → Settings → Stream</li>
                            <li>2. Service: <strong>Custom</strong></li>
                            <li>3. Server: Paste the <span className="text-orange-400">RTMPS URL</span></li>
                            <li>4. Stream Key: Paste the <span className="text-purple-400">Stream Key</span></li>
                            <li>5. Click "Start Streaming" when ready!</li>
                          </ol>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-400" />Customer Management
                  </CardTitle>
                  <CardDescription>{customers.length} total users • {moderators.length} admins/moderators</CardDescription>
                </div>
                <Button className="bg-purple-600" onClick={() => { resetCustomerForm(); setShowCustomerModal(true); }}>
                  <Plus className="h-4 w-4 mr-2" />Add Customer
                </Button>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs for Users and Admins */}
                <div className="flex gap-2 mb-4">
                  <Button 
                    variant={customerSubTab === 'users' ? 'default' : 'outline'}
                    className={customerSubTab === 'users' ? 'bg-purple-600 text-white' : 'border-white/20 text-white'}
                    onClick={() => { setCustomerSubTab('users'); setSelectedCustomers([]); }}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Users ({regularUsers.length})
                  </Button>
                  <Button 
                    variant={customerSubTab === 'admins' ? 'default' : 'outline'}
                    className={customerSubTab === 'admins' ? 'bg-white text-black' : 'border-white/20 text-white'}
                    onClick={() => { setCustomerSubTab('admins'); setSelectedCustomers([]); }}
                  >
                    <Shield className={`h-4 w-4 mr-2 ${customerSubTab === 'admins' ? 'text-yellow-600' : ''}`} />
                    Admins & Moderators ({adminUsers.length})
                  </Button>
                </div>

                {/* Bulk Actions Bar */}
                {(() => {
                  const filteredCustomers = customerSubTab === 'admins' ? adminUsers : regularUsers;
                  
                  return filteredCustomers.length > 0 && (
                    <div className="flex items-center gap-4 mb-4 p-3 bg-white/5 rounded-lg">
                      <label className="flex items-center gap-2 text-white cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                          onChange={() => handleSelectAll(filteredCustomers)}
                          className="w-4 h-4 rounded"
                        />
                        Select All
                      </label>
                      {selectedCustomers.length > 0 && (
                        <>
                          <span className="text-gray-400">|</span>
                          <span className="text-purple-400">{selectedCustomers.length} selected</span>
                          <select 
                            value={bulkAction} 
                            onChange={(e) => setBulkAction(e.target.value)}
                            className="bg-white/10 border border-white/20 rounded px-3 py-1 text-white"
                          >
                            <option value="">Choose action...</option>
                            {customerSubTab === 'users' && <option value="make_admin">Make Admin</option>}
                            {customerSubTab === 'users' && <option value="make_moderator">Make Moderator</option>}
                            {customerSubTab === 'admins' && <option value="remove_admin">Remove Admin</option>}
                            {customerSubTab === 'admins' && <option value="remove_moderator">Remove Moderator</option>}
                            <option value="delete">Delete</option>
                          </select>
                          <Button 
                            size="sm" 
                            className="bg-red-600 hover:bg-red-700"
                            onClick={handleBulkAction}
                            disabled={!bulkAction || loading}
                          >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Customer List */}
                <div className="space-y-3">
                  {(() => {
                    const filteredCustomers = customerSubTab === 'admins' ? adminUsers : regularUsers;
                    
                    if (filteredCustomers.length === 0) {
                      return (
                        <div className="text-center py-12">
                          {customerSubTab === 'admins' ? (
                            <>
                              <Shield className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                              <p className="text-gray-400">No admins or moderators yet.</p>
                              <p className="text-gray-500 text-sm mt-1">Promote users from the Users tab.</p>
                            </>
                          ) : (
                            <>
                              <Users className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                              <p className="text-gray-400">No regular users yet.</p>
                            </>
                          )}
                        </div>
                      );
                    }
                    
                    return filteredCustomers.map((customer) => {
                      const isMod = moderators.some(m => m.id === customer.id);
                      const isSelected = selectedCustomers.includes(customer.id);
                      return (
                        <div 
                          key={customer.id} 
                          className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                            isSelected ? 'bg-purple-500/20 border border-purple-500/50' : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <input 
                              type="checkbox" 
                              checked={isSelected}
                              onChange={() => handleSelectCustomer(customer.id)}
                              className="w-4 h-4 rounded"
                            />
                            {customer.profile_picture ? (
                              <img src={customer.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                <UserCircle className="h-6 w-6 text-white" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-white">{customer.full_name}</p>
                                {isMod && <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50"><Shield className="h-3 w-3 mr-1" />MOD</Badge>}
                                {customer.is_admin && <Badge className="bg-red-500/20 text-red-400 border-red-500/50">ADMIN</Badge>}
                              </div>
                              <p className="text-sm text-gray-400">{customer.email}</p>
                              {customer.username && <Badge variant="outline" className="mt-1 border-purple-500/50 text-purple-400">@{customer.username}</Badge>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <button
                                onClick={() => handleViewCustomerTickets(customer)}
                                className="font-bold text-white hover:text-purple-400 transition-colors cursor-pointer"
                              >
                                {customer.total_purchases || 0} tickets
                              </button>
                              <p className="text-sm text-green-400">£{(customer.total_spent || 0).toFixed(2)}</p>
                              {customer.purchases && customer.purchases.length > 0 && (
                                <p className="text-xs text-gray-500 truncate max-w-[150px]">
                                  {customer.purchases[0]?.event_title}
                                  {customer.purchases.length > 1 && ` +${customer.purchases.length - 1} more`}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                              onClick={() => handleViewCustomerTickets(customer)}
                              title="View & manage tickets"
                            >
                              <Ticket className="h-4 w-4" />
                            </Button>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className={customer.is_admin ? "border-red-500/50 text-red-400" : "border-white/20"}
                                onClick={() => toggleAdmin(customer.id, customer.is_admin)}
                                title={customer.is_admin ? "Remove admin" : "Make admin"}
                              >
                                <Crown className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className={isMod ? "border-yellow-500/50 text-yellow-400" : "border-white/20"}
                                onClick={() => toggleModerator(customer.id, isMod)}
                                title={isMod ? "Remove moderator" : "Make moderator"}
                              >
                                <Shield className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="border-white/20" onClick={() => handleEditCustomer(customer)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => handleDeleteCustomer(customer.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo">
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Tag className="h-5 w-5 text-green-400" />Promo Codes
                  </CardTitle>
                  <CardDescription>{promoCodes.length} total codes</CardDescription>
                </div>
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => { resetPromoForm(); setShowPromoModal(true); }}>
                  <Plus className="h-4 w-4 mr-2" />Create Promo Code
                </Button>
              </CardHeader>
              <CardContent>
                {promoCodes.length === 0 ? (
                  <div className="text-center py-12">
                    <Tag className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400">No promo codes yet. Create one to get started!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {promoCodes.map((promo) => (
                      <div key={promo.id} className="p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${promo.is_active ? 'bg-green-600/20' : 'bg-gray-600/20'}`}>
                              {promo.discount_type === 'percentage' ? (
                                <Percent className={`h-6 w-6 ${promo.is_active ? 'text-green-400' : 'text-gray-400'}`} />
                              ) : (
                                <DollarSign className={`h-6 w-6 ${promo.is_active ? 'text-green-400' : 'text-gray-400'}`} />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-white text-lg font-mono">{promo.code}</p>
                                {!promo.is_active && <Badge variant="outline" className="border-gray-500 text-gray-400">Inactive</Badge>}
                              </div>
                              <p className="text-sm text-gray-400">
                                {promo.discount_type === 'percentage' 
                                  ? `${promo.discount_value}% off` 
                                  : `£${promo.discount_value.toFixed(2)} off`
                                }
                                {' • '}
                                {promo.event_title}
                              </p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                {promo.usage_limit ? (
                                  <span>Used: {promo.usage_count || 0}/{promo.usage_limit}</span>
                                ) : (
                                  <span>Used: {promo.usage_count || 0} (unlimited)</span>
                                )}
                                {promo.expiry_date && (
                                  <span>Expires: {new Date(promo.expiry_date).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-green-500/30 text-green-400 hover:bg-green-500/20"
                              onClick={() => copyPromoLink(promo)}
                              title="Copy promo link"
                            >
                              <Link className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-white/20"
                              onClick={() => {
                                setEditingPromo(promo);
                                setPromoForm({
                                  code: promo.code,
                                  discount_type: promo.discount_type,
                                  discount_value: promo.discount_value.toString(),
                                  event_id: promo.event_id || '',
                                  usage_limit: promo.usage_limit?.toString() || '',
                                  expiry_date: promo.expiry_date ? promo.expiry_date.split('T')[0] : '',
                                  is_active: promo.is_active
                                });
                                setShowPromoModal(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-red-500/30 text-red-400"
                              onClick={() => handleDeletePromo(promo.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Moderation Tab */}
          <TabsContent value="chat">
            <ErrorBoundary>
            <div className="space-y-6">
              {/* Event Selector */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Select Event</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(events) && events.map(event => (
                      <Button 
                        key={event?.id || Math.random()} 
                        variant={selectedEvent?.id === event?.id ? "default" : "outline"}
                        className={selectedEvent?.id === event?.id ? "bg-white text-black hover:bg-gray-100" : "border-white/20 text-white"}
                        onClick={() => {
                          setSelectedEvent(event);
                          loadChatMessages(event?.id);
                        }}
                      >
                        {event?.title || 'Untitled Event'}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Chat Messages */}
              {selectedEvent && (
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-purple-400" />
                      Chat for: {selectedEvent?.title || 'Unknown Event'}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-white/20"
                        onClick={() => selectedEvent?.id && loadChatMessages(selectedEvent.id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />Refresh
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        onClick={() => selectedEvent?.id && clearAllChat(selectedEvent.id)}
                      >
                        <Eraser className="h-4 w-4 mr-2" />Clear All Chat
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingChat ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto" />
                        <p className="text-gray-500 mt-2">Loading messages...</p>
                      </div>
                    ) : !Array.isArray(chatMessages) || chatMessages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-500">No chat messages for this event yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[600px] overflow-y-auto">
                        <p className="text-sm text-gray-500 mb-4">{chatMessages.length} messages total</p>
                        {chatMessages.map((msg, idx) => (
                          <div key={msg?.id || `chat-msg-${idx}`} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg group hover:bg-white/10 transition-colors">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-purple-400 text-sm">@{msg?.username || 'unknown'}</span>
                                <span className="text-xs text-gray-500">
                                  {msg?.timestamp ? new Date(msg.timestamp).toLocaleString() : ''}
                                </span>
                              </div>
                              <p className="text-white text-sm mt-1">{msg?.message || ''}</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => msg?.id && deleteChatMessage(msg.id)}
                              title="Delete this message"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {!selectedEvent && (
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Select an event above to manage its chat</p>
                  </CardContent>
                </Card>
              )}
            </div>
            </ErrorBoundary>
          </TabsContent>

          {/* Live Viewers Tab */}
          <TabsContent value="viewers">
            <ErrorBoundary>
            <div className="space-y-6">
              {/* Event Selector */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Select Event</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {events.filter(e => e.status === 'live').map(event => (
                      <Button key={event.id} variant={selectedEvent?.id === event.id ? "default" : "outline"}
                        className={selectedEvent?.id === event.id ? "bg-white text-black hover:bg-gray-100" : "border-white/20 text-white"}
                        onClick={() => setSelectedEvent(event)}>
                        {event.title}
                      </Button>
                    ))}
                    {events.filter(e => e.status === 'live').length === 0 && (
                      <p className="text-gray-500">No live events. Viewers tracking is only available for live events.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {selectedEvent && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Viewer Count */}
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Eye className="h-5 w-5 text-green-400" />
                        Live Viewers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8">
                        <p className="text-6xl font-bold text-green-400">{liveViewers?.total_viewers || 0}</p>
                        <p className="text-gray-400 mt-2">watching now</p>
                        <Button variant="outline" size="sm" className="mt-4 bg-white text-black hover:bg-gray-100 border-white" onClick={loadLiveViewers}>
                          <RefreshCw className="h-4 w-4 mr-2" />Refresh
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Locations */}
                  <Card className="bg-white/5 border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-blue-400" />
                        Viewer Locations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(liveViewers?.locations?.length || 0) === 0 ? (
                        <p className="text-gray-500 text-center py-4">No location data yet</p>
                      ) : (
                        <div className="space-y-2">
                          {(liveViewers?.locations || []).map((loc, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                              <span className="text-white">{loc.city}, {loc.country}</span>
                              <Badge className="bg-blue-500">{loc.count}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Viewer List */}
                  <Card className="bg-white/5 border-white/10 lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-white">Active Viewers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(liveViewers?.viewers?.length || 0) === 0 ? (
                        <p className="text-gray-500 text-center py-4">No active viewers</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {(liveViewers?.viewers || []).map((viewer) => (
                            <div key={viewer.customer_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                              {viewer.customer?.profile_picture ? (
                                <img src={viewer.customer.profile_picture} alt="" className="w-10 h-10 rounded-full" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                  <UserCircle className="h-5 w-5 text-purple-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-white text-sm">{viewer.customer?.full_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{viewer.city || 'Unknown'}, {viewer.country || 'Unknown'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Chat Moderation */}
                  <Card className="bg-white/5 border-white/10 lg:col-span-2">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-purple-400" />
                        Chat Moderation
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-white/20"
                          onClick={() => selectedEvent?.id && loadChatMessages(selectedEvent.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />Refresh
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                          onClick={() => selectedEvent?.id && clearAllChat(selectedEvent.id)}
                        >
                          <Eraser className="h-4 w-4 mr-2" />Clear All
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingChat ? (
                        <div className="text-center py-8">
                          <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto" />
                        </div>
                      ) : !Array.isArray(chatMessages) || chatMessages.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No chat messages yet</p>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {chatMessages.map((msg, idx) => (
                            <div key={msg?.id || `viewer-chat-${idx}`} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg group">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-purple-400 text-sm">@{msg?.username || 'unknown'}</span>
                                  <span className="text-xs text-gray-500">
                                    {msg?.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                                  </span>
                                </div>
                                <p className="text-white text-sm mt-1">{msg?.message || ''}</p>
                              </div>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => msg?.id && deleteChatMessage(msg.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
            </ErrorBoundary>
          </TabsContent>

          {/* Voting Tab */}
          <TabsContent value="voting">
            <div className="space-y-6">
              {/* Event Selector */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white">Voting Questions</CardTitle>
                  {selectedEvent && (
                    <Button className="bg-purple-600" onClick={() => { setEditingQuestion(null); setQuestionForm({ question: '', options: ['', ''], is_active: true }); setShowQuestionModal(true); }}>
                      <Plus className="h-4 w-4 mr-2" />Add Question
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {events.map(event => (
                      <Button key={event.id} variant={selectedEvent?.id === event.id ? "default" : "outline"}
                        className={selectedEvent?.id === event.id ? "bg-white text-black hover:bg-gray-100" : "border-white/20 text-white"}
                        onClick={() => setSelectedEvent(event)}>
                        {event.title}
                      </Button>
                    ))}
                  </div>

                  {selectedEvent && votingQuestions.length === 0 && (
                    <div className="text-center py-8">
                      <HelpCircle className="h-12 w-12 mx-auto mb-4 text-white/20" />
                      <p className="text-gray-400">No voting questions yet</p>
                    </div>
                  )}

                  {selectedEvent && votingQuestions.length > 0 && (
                    <div className="space-y-6">
                      {votingQuestions.map((q) => (
                        <div key={q.id} className={`p-5 rounded-xl border ${q.is_active ? 'bg-purple-500/10 border-purple-500/30' : 'bg-white/5 border-white/10'}`}>
                          {/* Question Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h4 className="font-bold text-white text-lg">{q.question}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={q.is_active ? 'bg-green-500' : 'bg-gray-500'}>{q.is_active ? 'Active' : 'Inactive'}</Badge>
                                {q.winner_announced && (
                                  <Badge className="bg-yellow-500 text-black">🏆 Winner: {q.winner}</Badge>
                                )}
                                {q.results_visible && (
                                  <Badge className="bg-blue-500">Results Visible</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="border-white/20" onClick={() => { setEditingQuestion(q); setQuestionForm({ question: q.question, options: q.options, is_active: q.is_active }); setShowQuestionModal(true); }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="border-yellow-500/30 text-yellow-400" onClick={() => handleResetVotes(q.id)} title="Reset all votes">
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => handleDeleteQuestion(q.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Vote Results (Admin View) */}
                          <div className="space-y-2 mb-4">
                            {q.options.map((opt, idx) => {
                              const votes = q.vote_counts?.[opt] || 0;
                              const percentage = q.total_votes > 0 ? Math.round((votes / q.total_votes) * 100) : 0;
                              const isWinner = q.winner === opt;
                              return (
                                <div key={idx} className={`relative overflow-hidden rounded-lg ${isWinner ? 'ring-2 ring-yellow-400' : ''}`}>
                                  <div className={`absolute inset-0 ${idx === 0 ? 'bg-red-500/40' : 'bg-blue-500/40'}`} style={{ width: `${percentage}%` }} />
                                  <div className="relative p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {isWinner && <Crown className="h-4 w-4 text-yellow-400" />}
                                      <span className="text-white font-medium">{opt}</span>
                                    </div>
                                    <span className="font-bold text-white">{votes} votes ({percentage}%)</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-sm text-gray-400 mb-4">{q.total_votes || 0} total votes</p>

                          {/* Winner Announcement Section */}
                          <div className="border-t border-white/10 pt-4 mt-4">
                            <h5 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                              <Trophy className="h-4 w-4 text-yellow-400" />
                              Announce Winner
                            </h5>
                            
                            {!q.winner_announced ? (
                              <div className="flex flex-wrap gap-2">
                                {q.options.map((opt) => (
                                  <Button
                                    key={opt}
                                    onClick={() => announceWinner(q.id, opt, true)}
                                    disabled={announcingWinner}
                                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold"
                                  >
                                    {announcingWinner ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
                                    Announce "{opt}" as Winner
                                  </Button>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg px-4 py-2 flex items-center gap-2">
                                  <Crown className="h-5 w-5 text-yellow-400" />
                                  <span className="text-white font-bold">{q.winner}</span>
                                  <span className="text-gray-400">is the winner!</span>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                                  onClick={() => hideVotingResults(q.id)}
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Hide Winner
                                </Button>
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-500 mt-3">
                              {q.results_visible 
                                ? "✓ Results are visible to viewers on the live stream" 
                                : "Results are hidden from viewers until you announce a winner"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Scorecard Tab */}
          <TabsContent value="scorecard">
            <div className="space-y-6">
              {/* Event Selector */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="h-5 w-5 text-yellow-400" />
                    Live Scorecard
                  </CardTitle>
                  <CardDescription>Update scores in real-time during the stream</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {events.filter(e => e.status === 'live').map(event => (
                      <Button key={event.id} variant={selectedEvent?.id === event.id ? "default" : "outline"}
                        className={selectedEvent?.id === event.id ? "bg-yellow-600" : "border-white/20"}
                        onClick={() => setSelectedEvent(event)}>
                        {event.title}
                      </Button>
                    ))}
                    {events.filter(e => e.status === 'live').length === 0 && (
                      <p className="text-gray-500">No live events</p>
                    )}
                  </div>

                  {selectedEvent && (
                    <div className="space-y-6">
                      {/* Visibility Toggle */}
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                        <div>
                          <p className="font-medium text-white">Show Scorecard on Stream</p>
                          <p className="text-sm text-gray-400">Viewers will see the scorecard overlay</p>
                        </div>
                        <button onClick={() => setScorecard({ ...scorecard, is_visible: !scorecard.is_visible })}
                          className={`w-14 h-7 rounded-full transition-colors ${scorecard.is_visible ? 'bg-green-500' : 'bg-gray-600'}`}>
                          <div className={`w-6 h-6 rounded-full bg-white shadow transform transition-transform ${scorecard.is_visible ? 'translate-x-7' : 'translate-x-0.5'}`} />
                        </button>
                      </div>

                      {/* Mode Selector */}
                      <div className="flex gap-3">
                        <button
                          onClick={() => setScorecard({ ...scorecard, mode: 'open' })}
                          className={`flex-1 p-4 rounded-xl border-2 transition-all ${scorecard.mode === 'open' ? 'border-yellow-500 bg-yellow-500/10' : 'border-white/10 hover:border-white/20'}`}
                        >
                          <Users className={`h-6 w-6 mx-auto mb-2 ${scorecard.mode === 'open' ? 'text-yellow-400' : 'text-gray-500'}`} />
                          <p className={`font-bold ${scorecard.mode === 'open' ? 'text-white' : 'text-gray-400'}`}>Open Round</p>
                          <p className="text-xs text-gray-500">Multiple contestants</p>
                        </button>
                        <button
                          onClick={() => setScorecard({ ...scorecard, mode: 'battle' })}
                          className={`flex-1 p-4 rounded-xl border-2 transition-all ${scorecard.mode === 'battle' ? 'border-red-500 bg-red-500/10' : 'border-white/10 hover:border-white/20'}`}
                        >
                          <Target className={`h-6 w-6 mx-auto mb-2 ${scorecard.mode === 'battle' ? 'text-red-400' : 'text-gray-500'}`} />
                          <p className={`font-bold ${scorecard.mode === 'battle' ? 'text-white' : 'text-gray-400'}`}>1v1 Battle</p>
                          <p className="text-xs text-gray-500">Head-to-head</p>
                        </button>
                      </div>

                      {/* Round Info */}
                      <div>
                        <Label className="text-gray-300">Round / Stage Info</Label>
                        <Input value={scorecard.round_info || ''} onChange={(e) => setScorecard({ ...scorecard, round_info: e.target.value })}
                          className="bg-white/5 border-white/20 text-white mt-2" placeholder="Round 1, Semi-Finals, etc." />
                      </div>

                      {/* Open Round Mode */}
                      {scorecard.mode === 'open' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-gray-300 text-lg">Contestants ({scorecard.contestants.length})</Label>
                          </div>

                          {/* Add Contestant */}
                          <div className="flex gap-2">
                            <Input 
                              value={newContestantName}
                              onChange={(e) => setNewContestantName(e.target.value)}
                              placeholder="Contestant name"
                              className="bg-white/5 border-white/20 text-white"
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addContestantToScorecard())}
                            />
                            <Button type="button" className="bg-yellow-600" onClick={addContestantToScorecard}>
                              <Plus className="h-4 w-4 mr-2" />Add
                            </Button>
                          </div>

                          {/* Contestant List */}
                          {scorecard.contestants.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                              <p>No contestants added yet</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {scorecard.contestants.map((contestant, index) => (
                                <div key={index} className="p-4 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl">
                                  <div className="flex items-center justify-between mb-3">
                                    <Input 
                                      value={contestant.name}
                                      onChange={(e) => updateContestantName(index, e.target.value)}
                                      className="bg-transparent border-none p-0 font-bold text-white text-lg focus:ring-0"
                                    />
                                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                      onClick={() => removeContestantFromScorecard(index)}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div className="flex items-center justify-center gap-3">
                                    <Button size="lg" variant="outline" className="h-10 w-10 rounded-full border-white/30"
                                      onClick={() => updateContestantScore(index, -1)}>-</Button>
                                    <span className="text-4xl font-bold text-yellow-400 min-w-[60px] text-center">{contestant.score}</span>
                                    <Button size="lg" className="h-10 w-10 rounded-full bg-yellow-600"
                                      onClick={() => updateContestantScore(index, 1)}>+</Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Battle Mode */}
                      {scorecard.mode === 'battle' && (
                        <div className="space-y-4">
                          {/* Select contestants for battle */}
                          {scorecard.contestants.length > 0 && (
                            <div className="p-4 bg-white/5 rounded-xl">
                              <p className="text-sm text-gray-400 mb-3">Select contestants for the battle (from Open Round roster)</p>
                              <div className="flex flex-wrap gap-2">
                                {scorecard.contestants.map((c, idx) => (
                                  <div key={idx} className="flex gap-1">
                                    <Button size="sm" variant="outline"
                                      className={`border-red-500/30 ${scorecard.battle_left?.name === c.name ? 'bg-red-500 text-white' : 'text-red-400'}`}
                                      onClick={() => setBattleContestant('left', c)}>
                                      {c.name} (L)
                                    </Button>
                                    <Button size="sm" variant="outline"
                                      className={`border-blue-500/30 ${scorecard.battle_right?.name === c.name ? 'bg-blue-500 text-white' : 'text-blue-400'}`}
                                      onClick={() => setBattleContestant('right', c)}>
                                      {c.name} (R)
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Battle Scorecard */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                            {/* Left Contestant */}
                            <div className={`text-center p-6 rounded-2xl ${scorecard.battle_left ? 'bg-red-500/10 border border-red-500/30' : 'bg-white/5 border border-dashed border-white/20'}`}>
                              {scorecard.battle_left ? (
                                <>
                                  <Input value={scorecard.battle_left.name}
                                    onChange={(e) => setScorecard({ ...scorecard, battle_left: { ...scorecard.battle_left, name: e.target.value } })}
                                    className="bg-transparent border-none text-center text-xl font-bold text-white mb-4" />
                                  <div className="flex items-center justify-center gap-4">
                                    <Button size="lg" variant="outline" className="h-12 w-12 rounded-full border-white/30"
                                      onClick={() => updateBattleScore('left', -1)}>-</Button>
                                    <span className="text-5xl font-bold text-red-400">{scorecard.battle_left.score}</span>
                                    <Button size="lg" className="h-12 w-12 rounded-full bg-red-600"
                                      onClick={() => updateBattleScore('left', 1)}>+</Button>
                                  </div>
                                  <Button size="sm" variant="ghost" className="mt-3 text-gray-400" onClick={() => setBattleContestant('left', null)}>
                                    <X className="h-4 w-4 mr-1" />Remove
                                  </Button>
                                </>
                              ) : (
                                <div className="py-8 text-gray-500">
                                  <UserCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                  <p>Select contestant</p>
                                </div>
                              )}
                            </div>

                            {/* VS */}
                            <div className="text-center">
                              <p className="text-5xl font-bold text-white/30">VS</p>
                            </div>

                            {/* Right Contestant */}
                            <div className={`text-center p-6 rounded-2xl ${scorecard.battle_right ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-white/5 border border-dashed border-white/20'}`}>
                              {scorecard.battle_right ? (
                                <>
                                  <Input value={scorecard.battle_right.name}
                                    onChange={(e) => setScorecard({ ...scorecard, battle_right: { ...scorecard.battle_right, name: e.target.value } })}
                                    className="bg-transparent border-none text-center text-xl font-bold text-white mb-4" />
                                  <div className="flex items-center justify-center gap-4">
                                    <Button size="lg" variant="outline" className="h-12 w-12 rounded-full border-white/30"
                                      onClick={() => updateBattleScore('right', -1)}>-</Button>
                                    <span className="text-5xl font-bold text-blue-400">{scorecard.battle_right.score}</span>
                                    <Button size="lg" className="h-12 w-12 rounded-full bg-blue-600"
                                      onClick={() => updateBattleScore('right', 1)}>+</Button>
                                  </div>
                                  <Button size="sm" variant="ghost" className="mt-3 text-gray-400" onClick={() => setBattleContestant('right', null)}>
                                    <X className="h-4 w-4 mr-1" />Remove
                                  </Button>
                                </>
                              ) : (
                                <div className="py-8 text-gray-500">
                                  <UserCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                  <p>Select contestant</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Save Button */}
                      <Button className="w-full bg-gradient-to-r from-yellow-600 to-orange-500 h-12" onClick={handleSaveScorecard} disabled={savingScorecard}>
                        {savingScorecard ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Save className="h-5 w-5 mr-2" />}
                        Update Scorecard Live
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Login Wallpapers Tab */}
          <TabsContent value="wallpapers">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Image className="h-5 w-5" />
                      Login Page Wallpapers
                    </CardTitle>
                    <CardDescription className="text-gray-400 mt-1">
                      Upload promotional images to display on the customer sign-in page
                    </CardDescription>
                  </div>
                  <Button 
                    className="bg-gradient-to-r from-purple-600 to-pink-500"
                    onClick={() => { setWallpaperForm({ title: '', image_url: '', event_id: '' }); setShowWallpaperModal(true); }}
                  >
                    <Plus className="h-4 w-4 mr-2" />Add Wallpaper
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Template Guide */}
                <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
                  <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-400" />
                    Design Template Guide
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                      <p className="text-gray-300 text-sm mb-3">
                        Upload separate images for <strong>desktop</strong> and <strong>mobile</strong> devices.
                        The login box sits in the <strong>center</strong> with a dark overlay.
                      </p>
                      <div className="space-y-2 text-sm text-gray-400">
                        <p><strong className="text-purple-400">Desktop:</strong> 1920 x 1080px (16:9 Landscape)</p>
                        <p><strong className="text-pink-400">Mobile:</strong> 1080 x 1920px (9:16 Portrait)</p>
                        <p><strong className="text-white">Safe Zone:</strong> Keep content around the <strong>edges</strong></p>
                        <p><strong className="text-white">Avoid:</strong> Don't put text/logos in the center</p>
                      </div>
                    </div>
                    {/* Desktop Preview */}
                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <p className="text-xs text-purple-400 mb-2 text-center flex items-center justify-center gap-1">
                        <Monitor className="h-3 w-3" /> Desktop Layout (1920x1080)
                      </p>
                      <div className="relative h-32 bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded border border-dashed border-purple-500/50">
                        <div className="absolute top-1 left-1 text-[8px] text-green-400">✓ Safe</div>
                        <div className="absolute top-1 right-1 text-[8px] text-green-400">✓ Safe</div>
                        <div className="absolute bottom-1 left-1 text-[8px] text-green-400">✓ Safe</div>
                        <div className="absolute bottom-1 right-1 text-[8px] text-green-400">✓ Safe</div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-20 bg-black/70 rounded border border-white/30 flex items-center justify-center">
                          <span className="text-[8px] text-white/70 text-center">Login<br/>Box</span>
                        </div>
                      </div>
                    </div>
                    {/* Mobile Preview */}
                    <div className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <p className="text-xs text-pink-400 mb-2 text-center flex items-center justify-center gap-1">
                        <Smartphone className="h-3 w-3" /> Mobile Layout (1080x1920)
                      </p>
                      <div className="relative h-32 w-20 mx-auto bg-gradient-to-br from-pink-900/30 to-purple-900/30 rounded border border-dashed border-pink-500/50">
                        <div className="absolute top-1 left-1 text-[6px] text-green-400">✓</div>
                        <div className="absolute top-1 right-1 text-[6px] text-green-400">✓</div>
                        <div className="absolute bottom-1 left-1 text-[6px] text-green-400">✓</div>
                        <div className="absolute bottom-1 right-1 text-[6px] text-green-400">✓</div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-14 h-16 bg-black/70 rounded border border-white/30 flex items-center justify-center">
                          <span className="text-[6px] text-white/70 text-center">Login</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Wallpapers Grid */}
                {wallpapers.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Image className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="mb-2">No wallpapers uploaded yet</p>
                    <p className="text-sm">Upload promotional images to make your login page stand out</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {wallpapers.map((wp) => (
                      <div 
                        key={wp.id} 
                        className={`relative group rounded-xl overflow-hidden border-2 transition-all ${
                          wp.is_active ? 'border-green-500 ring-2 ring-green-500/30' : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="aspect-video bg-black">
                          <img 
                            src={wp.image_url} 
                            alt={wp.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h4 className="font-bold text-white text-lg truncate">{wp.title}</h4>
                          <div className="flex items-center gap-2 mt-2">
                            {wp.is_active && (
                              <Badge className="bg-green-500 text-white">
                                <CheckCircle2 className="h-3 w-3 mr-1" />Active
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-gray-300 border-gray-600">
                              <Monitor className="h-3 w-3 mr-1" />Desktop
                            </Badge>
                            {wp.mobile_image_url && (
                              <Badge variant="outline" className="text-pink-300 border-pink-600">
                                <Smartphone className="h-3 w-3 mr-1" />Mobile
                              </Badge>
                            )}
                          </div>
                        </div>
                        {/* Actions overlay */}
                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!wp.is_active && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-500"
                              onClick={() => activateWallpaper(wp.id)}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteWallpaper(wp.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Deactivate All Button */}
                {wallpapers.some(w => w.is_active) && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <Button 
                      variant="outline" 
                      className="text-gray-400 border-white/20"
                      onClick={deactivateAllWallpapers}
                    >
                      <X className="h-4 w-4 mr-2" />Deactivate All (Show Default Dark Background)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cross-Platform Promotions Tab */}
          <TabsContent value="crosspromo">
            <PromoManagement />
          </TabsContent>

          {/* SEO Tab */}
          <TabsContent value="seo">
            <SEOAdmin platform="live" authToken={localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken')} />
          </TabsContent>

          {/* Stripe Accounts Tab */}
          <TabsContent value="stripe">
            <StripeAccountsManager />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Wallpaper Modal */}
      {showWallpaperModal && (
        <Modal title="Add Login Wallpaper" onClose={() => setShowWallpaperModal(false)}>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Title / Event Name</Label>
              <Input 
                value={wallpaperForm.title}
                onChange={(e) => setWallpaperForm({ ...wallpaperForm, title: e.target.value })}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="e.g., Summer Battle 2026"
                required
              />
            </div>
            
            {/* Desktop Image Upload */}
            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <Monitor className="h-4 w-4" />
                Desktop Image (1920 x 1080px)
              </Label>
              <div className="mt-2">
                {wallpaperForm.image_url ? (
                  <div className="relative rounded-lg overflow-hidden">
                    <img 
                      src={wallpaperForm.image_url} 
                      alt="Desktop Preview" 
                      className="w-full aspect-video object-cover"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => setWallpaperForm({ ...wallpaperForm, image_url: '' })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Desktop (16:9)
                    </div>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center cursor-pointer hover:border-purple-500/50 transition-colors"
                    onClick={() => wallpaperInputRef.current?.click()}
                  >
                    {uploadingWallpaper ? (
                      <Loader2 className="h-10 w-10 mx-auto mb-2 text-purple-400 animate-spin" />
                    ) : (
                      <Monitor className="h-10 w-10 mx-auto mb-2 text-gray-500" />
                    )}
                    <p className="text-gray-400 text-sm">Click to upload desktop image</p>
                    <p className="text-xs text-gray-600 mt-1">1920 x 1080px (Landscape)</p>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={wallpaperInputRef}
                  className="hidden" 
                  accept="image/*"
                  onChange={(e) => handleWallpaperUpload(e, false)}
                />
              </div>
            </div>

            {/* Mobile Image Upload */}
            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Mobile Image (1080 x 1920px) <span className="text-gray-500 text-xs">(Optional)</span>
              </Label>
              <div className="mt-2">
                {wallpaperForm.mobile_image_url ? (
                  <div className="relative rounded-lg overflow-hidden max-w-[200px]">
                    <img 
                      src={wallpaperForm.mobile_image_url} 
                      alt="Mobile Preview" 
                      className="w-full aspect-[9/16] object-cover"
                    />
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => setWallpaperForm({ ...wallpaperForm, mobile_image_url: '' })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Mobile (9:16)
                    </div>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center cursor-pointer hover:border-pink-500/50 transition-colors max-w-[200px]"
                    onClick={() => mobileWallpaperInputRef.current?.click()}
                  >
                    {uploadingMobileWallpaper ? (
                      <Loader2 className="h-10 w-10 mx-auto mb-2 text-pink-400 animate-spin" />
                    ) : (
                      <Smartphone className="h-10 w-10 mx-auto mb-2 text-gray-500" />
                    )}
                    <p className="text-gray-400 text-sm">Upload mobile</p>
                    <p className="text-xs text-gray-600 mt-1">1080 x 1920px (Portrait)</p>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={mobileWallpaperInputRef}
                  className="hidden" 
                  accept="image/*"
                  onChange={(e) => handleWallpaperUpload(e, true)}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">If no mobile image is uploaded, the desktop image will be used on mobile devices.</p>
            </div>

            <div>
              <Label className="text-gray-300">Link to Event (Optional)</Label>
              <select
                className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white"
                value={wallpaperForm.event_id}
                onChange={(e) => setWallpaperForm({ ...wallpaperForm, event_id: e.target.value })}
              >
                <option value="">No specific event</option>
                {events.map(e => (
                  <option key={e.id} value={e.id}>{e.title}</option>
                ))}
              </select>
            </div>

            <Button 
              className="w-full bg-gradient-to-r from-purple-600 to-pink-500"
              onClick={handleSaveWallpaper}
              disabled={!wallpaperForm.title || !wallpaperForm.image_url || savingWallpaper}
            >
              {savingWallpaper ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Wallpaper
            </Button>
          </div>
        </Modal>
      )}

      {/* Create/Edit Event Modal */}
      {showCreateModal && (
        <Modal title={editingEvent ? 'Edit Event' : 'Create Event'} onClose={() => { setShowCreateModal(false); resetForm(); }}>
          <form onSubmit={handleSubmit}>
            {/* Step indicators */}
            <div className="flex gap-2 mb-6">
              {['Details', 'Media', 'SEO', 'Voting', 'Branding'].map((s, i) => (
                <button key={s} type="button" onClick={() => setCreateStep(i + 1)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${createStep === i + 1 ? 'bg-white text-black' : 'bg-white/10 text-gray-400'}`}>{s}</button>
              ))}
            </div>

            {createStep === 1 && (
              <div className="space-y-4">
                <div><Label className="text-gray-300">Title</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required /></div>
                <div><Label className="text-gray-300">Description</Label><textarea className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white" rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-gray-300">Date & Time</Label><Input type="datetime-local" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required /></div>
                  <div><Label className="text-gray-300">Status</Label><select className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}><option value="upcoming">Upcoming</option><option value="live">Live</option><option value="ended">Ended</option></select></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-gray-300">Price</Label><Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required /></div>
                  <div><Label className="text-gray-300">Currency</Label><select className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white" value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })}><option value="GBP">GBP</option><option value="USD">USD</option></select></div>
                </div>
                
                {/* VOD Video Link */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Film className="h-4 w-4 text-cyan-400" />
                    Link VOD Recording
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-2">After the event ends, viewers will see a "Watch on VOD" button linking to this video.</p>
                  <select
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white"
                    value={formData.vod_video_id}
                    onChange={(e) => setFormData({ ...formData, vod_video_id: e.target.value })}
                    data-testid="vod-video-selector"
                  >
                    <option value="">No VOD linked</option>
                    {vodVideos.map((v) => (
                      <option key={v.id} value={v.id}>{v.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {createStep === 2 && (
              <div className="space-y-4">
                {/* Desktop Thumbnail */}
                <div className="bg-gradient-to-r from-slate-900/50 to-slate-800/50 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Monitor className="h-4 w-4 text-blue-400" />
                    <Label className="text-gray-300">Desktop Thumbnail</Label>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Recommended: <span className="text-blue-400 font-medium">1920 x 1080px</span> (16:9 ratio) • Max 10MB</p>
                  <div className="mt-2">{formData.thumbnail_url ? (
                    <div className="relative"><img src={formData.thumbnail_url} alt="" className="w-full h-40 object-cover rounded-xl" /><Button type="button" size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => setFormData({ ...formData, thumbnail_url: '' })}><X className="h-4 w-4" /></Button>
                      <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1"><Monitor className="h-3 w-3" /> Desktop</div>
                    </div>
                  ) : (
                    <div onClick={() => thumbnailInputRef.current?.click()} className="w-full h-40 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500">
                      {uploadingThumbnail ? <Loader2 className="h-8 w-8 animate-spin" /> : <><Monitor className="h-8 w-8 text-gray-500 mb-2" /><p className="text-gray-400 text-sm">Upload Desktop Thumbnail</p><p className="text-gray-500 text-xs mt-1">1920 x 1080px (16:9)</p></>}
                    </div>
                  )}</div>
                  <input ref={thumbnailInputRef} type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
                </div>

                {/* Mobile Thumbnail */}
                <div className="bg-gradient-to-r from-slate-900/50 to-slate-800/50 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="h-4 w-4 text-green-400" />
                    <Label className="text-gray-300">Mobile Thumbnail</Label>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Recommended: <span className="text-green-400 font-medium">1080 x 1920px</span> (9:16 ratio) • Max 10MB</p>
                  <div className="mt-2">{formData.mobile_thumbnail_url ? (
                    <div className="relative">
                      <img src={formData.mobile_thumbnail_url} alt="" className="w-32 h-56 object-cover rounded-xl mx-auto" />
                      <Button type="button" size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => setFormData({ ...formData, mobile_thumbnail_url: '' })}><X className="h-4 w-4" /></Button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1"><Smartphone className="h-3 w-3" /> Mobile</div>
                    </div>
                  ) : (
                    <div onClick={() => mobileThumbnailInputRef.current?.click()} className="w-32 h-56 mx-auto border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-green-500">
                      {uploadingMobileThumbnail ? <Loader2 className="h-8 w-8 animate-spin" /> : <><Smartphone className="h-8 w-8 text-gray-500 mb-2" /><p className="text-gray-400 text-sm text-center">Upload Mobile</p><p className="text-gray-500 text-xs mt-1 text-center">1080 x 1920px</p></>}
                    </div>
                  )}</div>
                  <input ref={mobileThumbnailInputRef} type="file" accept="image/*" onChange={handleMobileThumbnailUpload} className="hidden" />
                  <p className="text-xs text-gray-500 mt-2 text-center">If not uploaded, desktop thumbnail will be used on mobile</p>
                </div>

                {/* Social Share Image */}
                <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-xl p-4">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Share2 className="h-4 w-4 text-blue-400" />
                    Social Share Image (For Facebook, WhatsApp, etc.)
                  </Label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">
                    <span className="text-blue-400 font-medium">1200 x 630px</span> recommended for best preview on social media. 
                    If not uploaded, the thumbnail will be used.
                  </p>
                  <div className="mt-2">{formData.social_share_image ? (
                    <div className="relative">
                      <img src={formData.social_share_image} alt="" className="w-full h-32 object-cover rounded-xl" />
                      <Button type="button" size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => setFormData({ ...formData, social_share_image: '' })}>
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        Social Share Image (1200x630)
                      </div>
                    </div>
                  ) : (
                    <div onClick={() => socialImageInputRef.current?.click()} className="w-full h-32 border-2 border-dashed border-blue-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500/50 bg-blue-500/5">
                      {uploadingSocialImage ? <Loader2 className="h-8 w-8 animate-spin text-blue-400" /> : (
                        <>
                          <Share2 className="h-6 w-6 text-blue-400 mb-2" />
                          <p className="text-gray-400 text-sm">Upload social share image</p>
                          <p className="text-gray-500 text-xs mt-1">1200 x 630px • Appears when shared</p>
                        </>
                      )}
                    </div>
                  )}</div>
                  <input ref={socialImageInputRef} type="file" accept="image/*" onChange={handleSocialImageUpload} className="hidden" />
                </div>

                {/* Promo Video Section */}
                <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-gray-300 flex items-center gap-2">
                      <Film className="h-4 w-4 text-purple-400" />
                      Promo Video (Optional)
                    </Label>
                    <div 
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => setFormData({ ...formData, show_promo_video: !formData.show_promo_video })}
                    >
                      <span className="text-xs text-gray-400">Show on event page</span>
                      <div className={`w-10 h-5 rounded-full ${formData.show_promo_video ? 'bg-purple-500' : 'bg-gray-600'}`}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${formData.show_promo_video ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`} />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Upload a promotional video to showcase your event. Videos are stored in Cloudflare Stream.
                  </p>
                  <div className="mt-2">
                    {formData.promo_video_url ? (
                      <div className="relative">
                        <div className="aspect-video bg-black rounded-xl overflow-hidden flex items-center justify-center">
                          {/* Show video thumbnail with play overlay */}
                          <div className="relative w-full h-full">
                            <img 
                              src={`https://customer-xg4xjbetrvqn62vp.cloudflarestream.com/${formData.promo_video_url.match(/([a-f0-9]{32})/)?.[1] || ''}/thumbnails/thumbnail.jpg`}
                              alt="Video preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                              <div className="text-center">
                                <Play className="w-12 h-12 text-white mx-auto mb-2" />
                                <p className="text-white text-sm">Video Ready</p>
                                <p className="text-gray-400 text-xs mt-1 max-w-xs truncate px-4">{formData.promo_video_url}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-2 right-2 z-10"
                          onClick={() => setFormData({ ...formData, promo_video_url: '', show_promo_video: false })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1 z-10">
                          <Film className="h-3 w-3" /> Promo Video
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div 
                          onClick={() => promoVideoInputRef.current?.click()} 
                          className="w-full h-32 border-2 border-dashed border-purple-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/50 bg-purple-500/5"
                        >
                          {uploadingPromoVideo ? (
                            <div className="text-center">
                              <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto mb-2" />
                              <p className="text-purple-400 text-sm">Uploading... {promoVideoProgress}%</p>
                              <div className="w-32 h-2 bg-gray-700 rounded-full mt-2 mx-auto overflow-hidden">
                                <div className="h-full bg-purple-500 transition-all" style={{ width: `${promoVideoProgress}%` }} />
                              </div>
                            </div>
                          ) : (
                            <>
                              <Film className="h-8 w-8 text-purple-400 mb-2" />
                              <p className="text-gray-400 text-sm">Click to upload promo video</p>
                              <p className="text-gray-500 text-xs mt-1">MP4, MOV • Max 1GB • 15 min max</p>
                            </>
                          )}
                        </div>
                        <div className="text-center">
                          <span className="text-gray-500 text-xs">— OR —</span>
                        </div>
                        <div>
                          <Input
                            placeholder="Paste Cloudflare Stream URL"
                            value={formData.promo_video_url}
                            onChange={(e) => setFormData({ ...formData, promo_video_url: e.target.value, show_promo_video: !!e.target.value })}
                            className="bg-white/5 border-white/10 text-white text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <input 
                    ref={promoVideoInputRef} 
                    type="file" 
                    accept="video/*" 
                    onChange={handlePromoVideoUpload} 
                    className="hidden" 
                  />
                </div>

                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Cloud className="h-5 w-5 text-green-400" />
                    <span className="text-green-400 font-medium">Cloudflare Stream</span>
                  </div>
                  <p className="text-gray-400 text-sm">A live stream URL and key will be automatically created when you save this event.</p>
                </div>
                <div><Label className="text-gray-300">Stripe Account</Label>
                  <select
                    value={formData.stripe_account}
                    onChange={(e) => setFormData({ ...formData, stripe_account: e.target.value })}
                    className="w-full mt-1 p-3 bg-gray-800 border border-white/10 rounded-lg text-white cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    <option value="default" className="bg-gray-800 text-white">Default (Main LTV Account)</option>
                    {stripeAccounts.filter(acc => acc.account_name !== 'default').map(acc => (
                      <option key={acc.account_name} value={acc.account_name} className="bg-gray-800 text-white">
                        {acc.display_name || acc.account_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select which Stripe account should receive payments for this event</p>
                </div>
              </div>
            )}

            {createStep === 3 && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/30 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SearchCode className="h-5 w-5 text-green-400" />
                    <h3 className="font-medium text-white">Search Engine Optimization</h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    Optimize your event for Google, Bing, and social media sharing to maximize ticket sales.
                  </p>
                </div>

                {/* SEO Title */}
                <div>
                  <Label className="text-gray-300 flex items-center justify-between">
                    <span>SEO Title</span>
                    <span className={`text-xs ${(formData.seo_title || formData.title).length > 60 ? 'text-red-400' : 'text-gray-500'}`}>
                      {(formData.seo_title || formData.title).length}/60
                    </span>
                  </Label>
                  <Input 
                    value={formData.seo_title} 
                    onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })} 
                    className="bg-white/5 border-white/10 text-white mt-1" 
                    placeholder={formData.title || "Event title for search engines"}
                    maxLength={70}
                  />
                  <p className="text-xs text-gray-500 mt-1">This appears in Google search results. 50-60 characters recommended.</p>
                </div>

                {/* SEO Description */}
                <div>
                  <Label className="text-gray-300 flex items-center justify-between">
                    <span>Meta Description</span>
                    <span className={`text-xs ${(formData.seo_description || formData.description).length > 160 ? 'text-red-400' : 'text-gray-500'}`}>
                      {(formData.seo_description || formData.description).length}/160
                    </span>
                  </Label>
                  <textarea 
                    className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm" 
                    rows={3}
                    value={formData.seo_description} 
                    onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                    placeholder={formData.description || "Describe your event for search results..."}
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">Shows below title in search results. 120-160 characters recommended.</p>
                </div>

                {/* Keywords */}
                <div>
                  <Label className="text-gray-300">Keywords</Label>
                  <Input 
                    value={formData.seo_keywords} 
                    onChange={(e) => setFormData({ ...formData, seo_keywords: e.target.value })} 
                    className="bg-white/5 border-white/10 text-white mt-1" 
                    placeholder="live stream, concert, music, event, tickets"
                  />
                  <p className="text-xs text-gray-500 mt-1">Comma-separated keywords to help search engines understand your content.</p>
                </div>

                {/* Open Graph (Social Sharing) */}
                <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-xl p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Share2 className="h-4 w-4 text-blue-400" />
                    <Label className="text-gray-300">Social Media Sharing (Facebook, WhatsApp, Twitter)</Label>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-400 text-sm">Social Share Title</Label>
                      <Input 
                        value={formData.og_title} 
                        onChange={(e) => setFormData({ ...formData, og_title: e.target.value })} 
                        className="bg-white/5 border-white/10 text-white mt-1" 
                        placeholder={formData.seo_title || formData.title || "Title when shared on social media"}
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Social Share Description</Label>
                      <textarea 
                        className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm" 
                        rows={2}
                        value={formData.og_description} 
                        onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                        placeholder={formData.seo_description || formData.description || "Description when shared"}
                      />
                    </div>
                  </div>
                </div>

                {/* Google Search Preview */}
                <div className="mt-4">
                  <Label className="text-gray-300 mb-2 block">Google Search Preview</Label>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-blue-800 text-lg hover:underline cursor-pointer line-clamp-1">
                      {formData.seo_title || formData.title || 'Event Title'}
                    </div>
                    <div className="text-green-700 text-sm">
                      ltvproduction.com/live/event/...
                    </div>
                    <div className="text-gray-600 text-sm line-clamp-2 mt-1">
                      {formData.seo_description || formData.description || 'Add a meta description to see how your event will appear in search results...'}
                    </div>
                  </div>
                </div>

                {/* Social Share Preview */}
                <div className="mt-4">
                  <Label className="text-gray-300 mb-2 block">Social Share Preview</Label>
                  <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 max-w-sm">
                    {formData.social_share_image || formData.thumbnail_url ? (
                      <img 
                        src={formData.social_share_image || formData.thumbnail_url} 
                        alt="Share Preview" 
                        className="w-full h-32 object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-full h-32 bg-gray-700 flex items-center justify-center">
                        <Globe className="h-8 w-8 text-gray-500" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="font-medium text-white line-clamp-1">{formData.og_title || formData.seo_title || formData.title || 'Event Title'}</p>
                      <p className="text-sm text-gray-400 line-clamp-2">{formData.og_description || formData.seo_description || formData.description || 'Description'}</p>
                      <p className="text-xs text-gray-500 mt-1">ltvproduction.com</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {createStep === 4 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl cursor-pointer" onClick={() => setFormData({ ...formData, voting_enabled: !formData.voting_enabled })}>
                  <div><p className="font-medium text-white">Enable Voting</p><p className="text-sm text-gray-400">Let viewers vote during stream</p></div>
                  <div className={`w-12 h-6 rounded-full ${formData.voting_enabled ? 'bg-purple-500' : 'bg-gray-600'}`}><div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${formData.voting_enabled ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} /></div>
                </div>
                {formData.voting_enabled && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between"><Label className="text-gray-300">Contestants</Label><Button type="button" size="sm" variant="outline" onClick={addContestant}><Plus className="h-4 w-4 mr-1" />Add</Button></div>
                    {votingContestants.map((c, idx) => (
                      <div key={idx} className="flex gap-3 p-3 bg-white/5 rounded-lg">
                        <Input value={c.name} onChange={(e) => updateContestant(idx, 'name', e.target.value)} className="flex-1 bg-white/5 border-white/10 text-white" placeholder={`Contestant ${idx + 1}`} />
                        {votingContestants.length > 2 && <Button type="button" size="sm" variant="outline" className="text-red-400" onClick={() => removeContestant(idx)}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Custom Branding */}
            {createStep === 5 && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-purple-400" />
                    <h3 className="font-medium text-white">Custom Branding</h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    Customize the event experience with the promoter's logo and branding. These will appear on the ticket page, sign-in page, and watch page.
                  </p>
                </div>

                {/* Custom Logo Upload */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Promoter Logo
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    This logo will replace the LTV Live logo on this event's pages.
                  </p>
                  <div className="mt-2">
                    {formData.custom_logo_url ? (
                      <div className="relative inline-block">
                        <div className="w-48 h-24 bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center p-4 border border-white/10">
                          <img 
                            src={formData.custom_logo_url} 
                            alt="Custom Logo" 
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2"
                          onClick={() => setFormData({ ...formData, custom_logo_url: '' })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => customLogoInputRef.current?.click()} 
                        className="w-48 h-24 border-2 border-dashed border-purple-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/50 bg-purple-500/5"
                      >
                        {uploadingCustomLogo ? (
                          <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                        ) : (
                          <>
                            <Upload className="h-6 w-6 text-purple-400 mb-1" />
                            <p className="text-gray-400 text-xs">Upload Logo</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <input 
                    ref={customLogoInputRef} 
                    type="file" 
                    accept="image/*" 
                    onChange={handleCustomLogoUpload} 
                    className="hidden" 
                  />
                </div>

                {/* Custom Sign-in Wallpaper Upload */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Sign-in Page Wallpaper
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Background image for the sign-in page when users access this event. Recommended: 1920x1080 or larger.
                  </p>
                  <div className="mt-2">
                    {formData.custom_signin_wallpaper_url ? (
                      <div className="relative inline-block">
                        <div className="w-64 h-36 bg-gray-900 rounded-xl overflow-hidden border border-white/10">
                          <img 
                            src={formData.custom_signin_wallpaper_url} 
                            alt="Sign-in Wallpaper" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2"
                          onClick={() => setFormData({ ...formData, custom_signin_wallpaper_url: '' })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => customWallpaperInputRef.current?.click()} 
                        className="w-64 h-36 border-2 border-dashed border-purple-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/50 bg-purple-500/5"
                      >
                        {uploadingCustomWallpaper ? (
                          <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                        ) : (
                          <>
                            <Upload className="h-6 w-6 text-purple-400 mb-1" />
                            <p className="text-gray-400 text-xs">Upload Wallpaper</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <input 
                    ref={customWallpaperInputRef} 
                    type="file" 
                    accept="image/*" 
                    onChange={handleCustomWallpaperUpload} 
                    className="hidden" 
                  />
                </div>

                {/* Mobile Sign-in Page Wallpaper */}
                <div>
                  <p className="text-sm font-medium text-white flex items-center gap-2 mb-1">
                    <Smartphone className="h-4 w-4 text-purple-400" />
                    Mobile Sign-in Wallpaper
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Background image for mobile users. Recommended: 1080×1920 (portrait).
                  </p>
                  <div>
                    {formData.custom_signin_wallpaper_mobile_url ? (
                      <div className="relative inline-block">
                        <img 
                          src={formData.custom_signin_wallpaper_mobile_url} 
                          alt="Mobile Wallpaper Preview" 
                          className="h-48 w-28 object-cover rounded-xl border border-purple-500/30"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2"
                          onClick={() => setFormData({ ...formData, custom_signin_wallpaper_mobile_url: '' })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        onClick={() => customWallpaperMobileInputRef.current?.click()} 
                        className="w-28 h-48 border-2 border-dashed border-purple-500/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/50 bg-purple-500/5"
                      >
                        {uploadingCustomWallpaperMobile ? (
                          <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                        ) : (
                          <>
                            <Smartphone className="h-6 w-6 text-purple-400 mb-1" />
                            <p className="text-gray-400 text-xs text-center px-1">Upload Mobile</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <input 
                    ref={customWallpaperMobileInputRef} 
                    type="file" 
                    accept="image/*" 
                    onChange={handleCustomWallpaperMobileUpload} 
                    className="hidden" 
                  />
                </div>

                {/* Restricted Navigation Toggle */}
                <div className="flex items-start justify-between gap-4 p-4 bg-white/5 rounded-xl cursor-pointer" onClick={() => setFormData({ ...formData, restricted_navigation: !formData.restricted_navigation })}>
                  <div className="flex-1">
                    <p className="font-medium text-white flex items-center gap-2">
                      <Lock className="h-4 w-4 text-amber-400 flex-shrink-0" />
                      Restricted Navigation
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Users from this event link can only access ticket page, my tickets, watch page, and account
                    </p>
                  </div>
                  <div className={`flex-shrink-0 w-12 h-6 rounded-full ${formData.restricted_navigation ? 'bg-amber-500' : 'bg-gray-600'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${formData.restricted_navigation ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} />
                  </div>
                </div>

                {formData.restricted_navigation && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <p className="text-sm text-amber-200">
                      <strong>When enabled:</strong> Users who arrive via this event's link will have limited navigation. They can only view this event, their tickets, the watch page, and their account. They cannot browse other events or the main site.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              {createStep > 1 && <Button type="button" variant="outline" className="border-white/20" onClick={() => setCreateStep(createStep - 1)}>Back</Button>}
              <div className="flex-1" />
              {/* When editing, show Update button on all tabs. When creating, show Continue until last step */}
              {editingEvent ? (
                <>
                  {createStep < 5 && (
                    <Button type="button" variant="outline" className="border-white/20" onClick={() => setCreateStep(createStep + 1)}>Next</Button>
                  )}
                  <Button type="submit" className="bg-red-600" disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Update
                  </Button>
                </>
              ) : (
                createStep < 5 ? (
                  <Button type="button" className="bg-white text-black" onClick={() => setCreateStep(createStep + 1)}>Continue</Button>
                ) : (
                  <Button type="submit" className="bg-red-600" disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create
                  </Button>
                )
              )}
            </div>
          </form>
        </Modal>
      )}

      {/* Customer Modal */}
      {showCustomerModal && (
        <Modal title={editingCustomer ? 'Edit Customer' : 'Add Customer'} onClose={() => { setShowCustomerModal(false); resetCustomerForm(); }}>
          <form onSubmit={handleSaveCustomer} className="space-y-4">
            <div><Label className="text-gray-300">Full Name</Label><Input value={customerForm.full_name} onChange={(e) => setCustomerForm({ ...customerForm, full_name: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required /></div>
            <div><Label className="text-gray-300">Email</Label><Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required /></div>
            <div><Label className="text-gray-300">Username</Label><Input value={customerForm.username} onChange={(e) => setCustomerForm({ ...customerForm, username: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-gray-300">{editingCustomer ? 'New Password (leave blank to keep)' : 'Password'}</Label><Input type="password" value={customerForm.password} onChange={(e) => setCustomerForm({ ...customerForm, password: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required={!editingCustomer} /></div>
            <Button type="submit" className="w-full bg-purple-600" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingCustomer ? 'Update' : 'Create'}</Button>
          </form>
        </Modal>
      )}

      {/* Question Modal */}
      {showQuestionModal && (
        <Modal title={editingQuestion ? 'Edit Question' : 'Add Question'} onClose={() => { setShowQuestionModal(false); setEditingQuestion(null); }}>
          <form onSubmit={handleSaveQuestion} className="space-y-4">
            <div><Label className="text-gray-300">Question</Label><Input value={questionForm.question} onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" required /></div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-gray-300">Options</Label><Button type="button" size="sm" variant="outline" onClick={() => setQuestionForm({ ...questionForm, options: [...questionForm.options, ''] })}><Plus className="h-4 w-4" /></Button></div>
              {questionForm.options.map((opt, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input value={opt} onChange={(e) => { const opts = [...questionForm.options]; opts[idx] = e.target.value; setQuestionForm({ ...questionForm, options: opts }); }} className="bg-white/5 border-white/10 text-white" placeholder={`Option ${idx + 1}`} />
                  {questionForm.options.length > 2 && <Button type="button" size="sm" variant="outline" className="text-red-400" onClick={() => setQuestionForm({ ...questionForm, options: questionForm.options.filter((_, i) => i !== idx) })}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-white">Active</span>
              <button type="button" onClick={() => setQuestionForm({ ...questionForm, is_active: !questionForm.is_active })} className={`w-12 h-6 rounded-full ${questionForm.is_active ? 'bg-green-500' : 'bg-gray-600'}`}><div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${questionForm.is_active ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} /></button>
            </div>
            <Button type="submit" className="w-full bg-purple-600" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingQuestion ? 'Update' : 'Create'}</Button>
          </form>
        </Modal>
      )}

      {/* Promo Code Modal */}
      {showPromoModal && (
        <Modal title={editingPromo ? 'Edit Promo Code' : 'Create Promo Code'} onClose={() => { setShowPromoModal(false); resetPromoForm(); }}>
          <form onSubmit={handleSavePromo} className="space-y-4">
            <div>
              <Label className="text-gray-300">Promo Code</Label>
              <Input 
                value={promoForm.code} 
                onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} 
                className="bg-white/5 border-white/10 text-white mt-1 font-mono uppercase" 
                placeholder="e.g., SUMMER20"
                required 
                disabled={editingPromo}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Discount Type</Label>
                <select 
                  className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white" 
                  value={promoForm.discount_type} 
                  onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (£)</option>
                </select>
              </div>
              <div>
                <Label className="text-gray-300">
                  {promoForm.discount_type === 'percentage' ? 'Discount (%)' : 'Amount (£)'}
                </Label>
                <Input 
                  type="number" 
                  step={promoForm.discount_type === 'percentage' ? '1' : '0.01'}
                  min="0"
                  max={promoForm.discount_type === 'percentage' ? '100' : undefined}
                  value={promoForm.discount_value} 
                  onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })} 
                  className="bg-white/5 border-white/10 text-white mt-1" 
                  placeholder={promoForm.discount_type === 'percentage' ? '20' : '5.00'}
                  required 
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Apply to Event</Label>
              <select 
                className="w-full mt-1 p-3 bg-white/5 border border-white/10 rounded-lg text-white" 
                value={promoForm.event_id} 
                onChange={(e) => setPromoForm({ ...promoForm, event_id: e.target.value })}
              >
                <option value="">All Events (Global)</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>{event.title}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Usage Limit</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={promoForm.usage_limit} 
                  onChange={(e) => setPromoForm({ ...promoForm, usage_limit: e.target.value })} 
                  className="bg-white/5 border-white/10 text-white mt-1" 
                  placeholder="Unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
              </div>
              <div>
                <Label className="text-gray-300">Expiry Date</Label>
                <Input 
                  type="date" 
                  value={promoForm.expiry_date} 
                  onChange={(e) => setPromoForm({ ...promoForm, expiry_date: e.target.value })} 
                  className="bg-white/5 border-white/10 text-white mt-1" 
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-white">Active</span>
              <button 
                type="button" 
                onClick={() => setPromoForm({ ...promoForm, is_active: !promoForm.is_active })} 
                className={`w-12 h-6 rounded-full ${promoForm.is_active ? 'bg-green-500' : 'bg-gray-600'}`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${promoForm.is_active ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`} />
              </button>
            </div>

            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={savingPromo}>
              {savingPromo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Tag className="h-4 w-4 mr-2" />}
              {editingPromo ? 'Update Promo Code' : 'Create Promo Code'}
            </Button>
          </form>
        </Modal>
      )}

      {/* Customer Detail / Ticket Management Modal */}
      {showCustomerDetailModal && selectedCustomerDetail && (
        <Modal title="Customer Tickets" onClose={() => { setShowCustomerDetailModal(false); setSelectedCustomerDetail(null); }}>
          <div className="space-y-4">
            {/* Customer Info */}
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl font-bold">
                {selectedCustomerDetail.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-white text-lg">{selectedCustomerDetail.full_name}</h3>
                <p className="text-gray-400 text-sm">{selectedCustomerDetail.email}</p>
                {selectedCustomerDetail.username && (
                  <Badge variant="outline" className="mt-1 border-purple-500/50 text-purple-400">@{selectedCustomerDetail.username}</Badge>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{customerTickets.length}</p>
                <p className="text-xs text-gray-500">tickets</p>
              </div>
            </div>

            {/* Add Ticket Button */}
            <Button 
              className="w-full bg-gradient-to-r from-green-600 to-green-500"
              onClick={() => setShowGrantTicketModal(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Grant Ticket to Customer
            </Button>

            {/* Tickets List */}
            <div className="space-y-3">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Ticket className="h-4 w-4 text-purple-400" />
                Purchased Tickets
              </h4>
              
              {loadingCustomerTickets ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                </div>
              ) : customerTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Ticket className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No tickets found</p>
                  <p className="text-sm">Grant a ticket using the button above</p>
                </div>
              ) : (
                customerTickets.map((ticket) => (
                  <div key={ticket.id} className="p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Event Thumbnail */}
                      {ticket.event?.thumbnail_url ? (
                        <img 
                          src={ticket.event.thumbnail_url} 
                          alt={ticket.event?.title} 
                          className="w-20 h-14 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-20 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                          <Play className="h-6 w-6 text-white" />
                        </div>
                      )}
                      
                      {/* Ticket Info */}
                      <div className="flex-1 min-w-0">
                        <h5 className="font-bold text-white truncate">{ticket.event?.title || 'Unknown Event'}</h5>
                        <div className="flex items-center gap-3 mt-1 text-sm">
                          <Badge variant="outline" className={`
                            ${ticket.event?.status === 'live' ? 'border-red-500 text-red-400' : 
                              ticket.event?.status === 'upcoming' ? 'border-blue-500 text-blue-400' : 
                              'border-gray-500 text-gray-400'}
                          `}>
                            {ticket.event?.status || 'unknown'}
                          </Badge>
                          {ticket.payment_method === 'admin_grant' ? (
                            <span className="text-yellow-400 text-xs flex items-center gap-1">
                              <Crown className="h-3 w-3" />Admin Granted
                            </span>
                          ) : (
                            <span className="text-green-400">£{(ticket.amount_paid || 0).toFixed(2)}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {ticket.purchased_at ? new Date(ticket.purchased_at).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : 'Unknown date'}
                        </p>
                      </div>
                      
                      {/* Revoke Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        onClick={() => handleRevokeTicket(ticket.id, ticket.event?.title)}
                        title="Remove this ticket"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Grant Ticket Modal */}
      {showGrantTicketModal && selectedCustomerDetail && (
        <Modal title="Grant Ticket" onClose={() => setShowGrantTicketModal(false)}>
          <div className="space-y-4">
            <p className="text-gray-400">
              Select an event to grant a free ticket to <strong className="text-white">{selectedCustomerDetail.full_name}</strong>:
            </p>
            
            {/* Events List */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {events.filter(e => e.status !== 'ended').length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No upcoming or live events available</p>
                </div>
              ) : (
                events.filter(e => e.status !== 'ended').map((event) => {
                  // Check if customer already has ticket for this event
                  const hasTicket = customerTickets.some(t => t.event_id === event.id);
                  
                  return (
                    <div 
                      key={event.id} 
                      className={`p-4 rounded-xl flex items-center gap-4 transition-colors ${
                        hasTicket 
                          ? 'bg-green-500/10 border border-green-500/30 cursor-not-allowed' 
                          : 'bg-white/5 hover:bg-white/10 cursor-pointer'
                      }`}
                      onClick={() => !hasTicket && handleGrantTicket(event.id)}
                    >
                      {event.thumbnail_url ? (
                        <img 
                          src={event.thumbnail_url} 
                          alt={event.title} 
                          className="w-16 h-12 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                          <Play className="h-5 w-5 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h5 className="font-bold text-white truncate">{event.title}</h5>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-xs
                            ${event.status === 'live' ? 'border-red-500 text-red-400' : 
                              (event.status !== 'live' && event.event_date && new Date(event.event_date).setHours(23,59,59,999) < Date.now()) || event.status === 'completed' || event.status === 'ended' ? 'border-gray-500 text-gray-400' : 'border-blue-500 text-blue-400'}
                          `}>
                            {event.status === 'live' ? 'LIVE' : 
                              (event.status !== 'live' && event.event_date && new Date(event.event_date).setHours(23,59,59,999) < Date.now()) || event.status === 'completed' || event.status === 'ended' ? 'Past Event' : 'Upcoming'}
                          </Badge>
                          <span className="text-gray-400 text-sm">£{event.price}</span>
                        </div>
                      </div>
                      {hasTicket ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Has Ticket
                        </Badge>
                      ) : (
                        <Button size="sm" className="bg-green-600 hover:bg-green-500">
                          <Plus className="h-4 w-4 mr-1" />Grant
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Stream Control Panel */}
      {showStreamControlPanel && controlPanelEvent && (
        <StreamControlPanel
          event={controlPanelEvent}
          authToken={localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken')}
          onClose={() => {
            setShowStreamControlPanel(false);
            setControlPanelEvent(null);
          }}
        />
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 bg-[#111]">
        <h2 className="text-xl font-bold text-white">{title}</h2>
        <Button variant="ghost" size="sm" className="text-gray-400" onClick={onClose}><X className="h-5 w-5" /></Button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// Stat Card
const StatCard = ({ icon: Icon, label, value, color, pulse, subtext }) => {
  const colorConfig = {
    gray: { gradient: 'from-gray-600/30 to-gray-700/20', border: 'border-gray-500/30', iconBg: 'bg-gray-500/20', text: 'text-gray-400', subtext: 'text-gray-400' },
    blue: { gradient: 'from-blue-600/30 to-cyan-600/20', border: 'border-blue-500/30', iconBg: 'bg-blue-500/20', text: 'text-blue-400', subtext: 'text-blue-300' },
    red: { gradient: 'from-red-600/30 to-rose-600/20', border: 'border-red-500/30', iconBg: 'bg-red-500/20', text: 'text-red-400', subtext: 'text-red-300' },
    green: { gradient: 'from-green-600/30 to-emerald-600/20', border: 'border-green-500/30', iconBg: 'bg-green-500/20', text: 'text-green-400', subtext: 'text-green-300' },
    yellow: { gradient: 'from-yellow-600/30 to-orange-600/20', border: 'border-yellow-500/30', iconBg: 'bg-yellow-500/20', text: 'text-yellow-400', subtext: 'text-yellow-300' },
    purple: { gradient: 'from-purple-600/30 to-pink-600/20', border: 'border-purple-500/30', iconBg: 'bg-purple-500/20', text: 'text-purple-400', subtext: 'text-purple-300' },
    cyan: { gradient: 'from-cyan-600/30 to-teal-600/20', border: 'border-cyan-500/30', iconBg: 'bg-cyan-500/20', text: 'text-cyan-400', subtext: 'text-cyan-300' },
    pink: { gradient: 'from-pink-600/30 to-rose-600/20', border: 'border-pink-500/30', iconBg: 'bg-pink-500/20', text: 'text-pink-400', subtext: 'text-pink-300' }
  };
  const cfg = colorConfig[color] || colorConfig.gray;
  
  return (
    <div className={`bg-gradient-to-br ${cfg.gradient} rounded-xl p-4 border ${cfg.border}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-5 w-5 ${cfg.text} ${pulse ? 'animate-pulse' : ''}`} />
        <span className="text-gray-300 text-xs">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtext && <p className={`text-xs ${cfg.subtext} mt-1`}>{subtext}</p>}
    </div>
  );
};

// Event Card - Traffic Source Style
const EventCard = ({ event, onEdit, onDelete, onSelect, formatDate, getStatusColor, onSetupStream, streamSetup, creatingStream, onOpenControlPanel, onEndEvent }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden group hover:border-gray-700 transition-all">
    <div className="relative h-40 bg-gradient-to-br from-gray-800 to-gray-900">
      {event.thumbnail_url ? <img src={event.thumbnail_url} alt={event.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Video className="h-12 w-12 text-gray-600" /></div>}
      <div className="absolute top-3 left-3">
        {(() => {
          const isPast = event.status !== 'live' && event.event_date && new Date(event.event_date).setHours(23,59,59,999) < Date.now();
          const displayStatus = event.status === 'live' ? 'LIVE' : event.status === 'ended' ? 'ENDED' : isPast ? 'PAST EVENT' : event.status === 'completed' ? 'PAST EVENT' : 'UPCOMING';
          const badgeClass = event.status === 'live' ? 'bg-gradient-to-r from-red-500 to-rose-500' : event.status === 'ended' ? 'bg-gray-600' : isPast || event.status === 'completed' ? 'bg-gray-600' : 'bg-gradient-to-r from-blue-500 to-cyan-500';
          return (
            <Badge className={`${badgeClass} text-white border-0`}>
              {event.status === 'live' && <span className="w-2 h-2 bg-white rounded-full animate-pulse mr-1.5" />}
              {displayStatus}
            </Badge>
          );
        })()}
      </div>
      {event.voting_enabled && <div className="absolute top-3 right-3"><Badge className="bg-gradient-to-r from-purple-500 to-pink-500 border-0"><Trophy className="h-3 w-3 mr-1" />Voting</Badge></div>}
      {event.cloudflare_live_input_id && event.cloudflare_rtmps_url && <div className="absolute bottom-3 left-3"><Badge className="bg-gradient-to-r from-orange-500 to-amber-500 border-0"><Cloud className="h-3 w-3 mr-1" />Stream Ready</Badge></div>}
      <div className="absolute bottom-3 right-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold px-3 py-1 rounded-lg text-sm">{event.currency === 'GBP' ? '£' : '$'}{event.price.toFixed(2)}</div>
    </div>
    <div className="p-4">
      <h3 className="font-bold text-white mb-1 truncate">{event.title}</h3>
      <p className="text-sm text-gray-400 mb-3 flex items-center gap-2"><Calendar className="h-3.5 w-3.5" />{formatDate(event.event_date)}</p>
      <div className="flex items-center gap-4 text-sm mb-4">
        <div className="flex items-center gap-1.5 text-gray-400"><Users className="h-4 w-4 text-purple-400" />{event.tickets_sold || 0}</div>
        <div className="flex items-center gap-1.5 text-green-400"><DollarSign className="h-4 w-4" />£{(event.net_revenue || event.revenue || 0).toFixed(2)}</div>
      </div>
      
      {/* Stream Setup Info — only show if not archived */}
      {event.cloudflare_live_input_id && event.cloudflare_rtmps_url && event.status !== 'ended' && (
        <div className="mb-3 p-3 bg-gradient-to-br from-orange-600/20 to-amber-600/10 border border-orange-500/30 rounded-lg">
          <p className="text-xs text-orange-400 font-bold mb-1">RTMPS URL:</p>
          <p className="text-xs text-white break-all font-mono bg-gray-800 p-1 rounded">{event.cloudflare_rtmps_url}</p>
          <p className="text-xs text-orange-400 font-bold mt-2 mb-1">Stream Key:</p>
          <p className="text-xs text-white break-all font-mono bg-gray-800 p-1 rounded">{event.cloudflare_stream_key}</p>
          
          {/* Stream Control Panel Button */}
          <Button 
            size="sm"
            onClick={() => onOpenControlPanel(event)}
            className="w-full mt-3 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white"
          >
            <Radio className="h-4 w-4 mr-2" />
            Open Control Panel
          </Button>
        </div>
      )}

      {/* Ended event info */}
      {event.status === 'ended' && (
        <div className="mb-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-center">
          <p className="text-xs text-gray-400">Event ended{event.vod_video_id ? ' — VOD linked' : ''}</p>
        </div>
      )}
      
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600" onClick={() => window.open(`/live/event/${event.slug}`, '_blank')}><Eye className="h-4 w-4 mr-1" />View</Button>
        {/* End Event button — show for live or streaming events that haven't ended */}
        {event.cloudflare_live_input_id && event.status !== 'ended' && (
          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/20" onClick={() => onEndEvent(event)} data-testid="end-event-btn">
            <XCircle className="h-4 w-4 mr-1" />End
          </Button>
        )}
        {!event.cloudflare_live_input_id && event.status !== 'ended' && (
          <Button size="sm" variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/20" onClick={() => onSetupStream(event.id)} disabled={creatingStream}>
            {creatingStream ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
          </Button>
        )}
        <Button size="sm" variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800" onClick={onEdit}><Pencil className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/20" onClick={onDelete}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  </div>
);

export default function LivestreamAdminDashboardWithBoundary() {
  return (
    <ErrorBoundary>
      <LivestreamAdminDashboard />
    </ErrorBoundary>
  );
}
